import {
  Fragment,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { buildKalshiMarketUrl } from 'src/Common/utils/kalshiMarketUrl';
import {
  actionVerb,
  centsFmt,
  fillPhrase,
  partialFillPhrase,
  platformWithSide,
  sideTitle,
} from '../utils/orderPhrases';
import {
  useNotifications,
  useNotificationTypes,
} from '../api/useNotifications';
import { useNotificationsSocket } from '../hooks/useNotificationsSocket';
import {
  MAX_FEED_ROWS,
  mergeNotificationFeed,
  compareNotificationsDesc,
  insertDescending,
} from '../utils/notificationsFeed';
import {
  getNotificationDisplay,
  getNotificationDirection,
  DIRECTION_DISPLAY,
} from '../components/notifications/notification-display';
import {
  NotificationEvent,
  NotificationEventType,
  NotificationsFilter,
  SOCIAL_NOTIFICATION_TYPES,
  isSocialNotification,
} from '../types/notifications.types';
import { LiveMatchEventsTab } from '../components/matchEvents/LiveMatchEventsTab';

/**
 * Type-filter dropdown group: one entry per UNIQUE chip label.
 *
 * Multiple event types intentionally share a chip label (`filled`
 * covers all full-fill subtypes; `partial` covers all partial-fill
 * subtypes). Without grouping, the dropdown showed the same word
 * eight times in a row and the operator had no idea what was
 * different between each row. We collapse them so the dropdown
 * shows ONE "filled" and ONE "partial" — selecting a group toggles
 * every member type in `filter.types`. URL params still persist
 * the expanded list of types, so back/forward + bookmarks keep
 * working, and a URL with a partial subset still shows the group
 * as "active" (any-member-selected semantics).
 *
 * Direction is NOT a property of the type anymore — it's an explicit
 * `evt.direction` field owned by the dedicated Direction dropdown. So
 * the Type dropdown groups purely by chip label and a multi-type group
 * shows a "N kinds" hint; it no longer derives a per-type direction
 * set (a base type spans entry + unwind + system, which a single
 * direction tag would misrepresent).
 */
interface TypeFilterGroup {
  key: string;
  label: string;
  emoji: string;
  color: string;
  types: NotificationEventType[];
}

// Accepts `string[]` because `useNotificationTypes` ships raw strings
// from the `/notifications/types` endpoint (the backend may roll out
// a new type before the frontend type union is regenerated). The
// helper still casts member types as `NotificationEventType` since
// they flow into `filter.types` — the loose source type just keeps
// the boundary honest.
function buildTypeFilterGroups(allTypes: string[]): TypeFilterGroup[] {
  const groups = new Map<string, TypeFilterGroup>();
  for (const raw of allTypes) {
    const t = raw as NotificationEventType;
    const d = getNotificationDisplay(t);
    const label = d.label ?? raw;
    let g = groups.get(label);
    if (!g) {
      g = {
        key: label,
        label,
        emoji: d.emoji,
        color: d.color,
        types: [],
      };
      groups.set(label, g);
    }
    g.types.push(t);
  }
  return Array.from(groups.values());
}

const PRESET_RANGES: Array<{ label: string; minutes: number | null }> = [
  { label: 'All time', minutes: null },
  { label: 'Last 15m', minutes: 15 },
  { label: 'Last 1h', minutes: 60 },
  { label: 'Last 6h', minutes: 360 },
  { label: 'Last 24h', minutes: 60 * 24 },
];

/**
 * Severity chip uses MUI palette names; per-event-type accent comes
 * from `notification-display.ts`. Keeping severity as a separate
 * dimension lets us color the row's left border by event type while
 * still showing severity (info/warning/error) as a small chip — so
 * the operator sees "kind of event" (color) and "urgency" (chip
 * tone) at the same time without conflating them.
 */
const SEVERITY_CHIP_COLOR: Record<
  NotificationEvent['severity'],
  'default' | 'info' | 'warning' | 'error'
> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
};

function severityChipColor(s: NotificationEvent['severity']) {
  return SEVERITY_CHIP_COLOR[s] || 'default';
}

/**
 * Brand-ish colors per platform so the operator can tell at a glance
 * whether a row is about a Kalshi or Polymarket order without
 * reading the platform word. Picked one approach (colored letters,
 * not a logo) so summary lines stay text-only and copyable.
 *
 * Kalshi → emerald (their brand teal-green).
 * Polymarket → bright blue (their brand purple-blue).
 */
const PLATFORM_COLORS: Record<string, string> = {
  kalshi: '#00b894',
  polymarket: '#4a9eff',
};

/**
 * The order-phrase vocabulary (verbs, cents formatting, side/platform
 * tagging) now lives in `utils/orderPhrases.ts` so the sniper's match
 * feed composes from the SAME language instead of growing a second one.
 * Re-exported here because `centsFmt` was part of this module's public
 * surface before the move.
 */
export { centsFmt } from '../utils/orderPhrases';

/**
 * Build the canonical Kalshi market URL from a full market ticker via the
 * shared `buildKalshiMarketUrl` util (event-page form
 * `/markets/<series>/<series>/<event>?op_market_ticker=<MARKET>`). The
 * event ticker is derived heuristically — notifications only carry the
 * market ticker.
 */
function kalshiMarketUrlFromTicker(
  ticker: string | null | undefined,
): string | null {
  if (!ticker) return null;
  return buildKalshiMarketUrl(ticker);
}

/**
 * Build the market URLs for an event. Mirrors the Slack message
 * builder's `marketLinks()` helper (services/position-unwinder/.../
 * slack-message.builder.ts) so both surfaces deep-link to the same
 * pages. Returns up to two entries — Kalshi + Polymarket — whichever
 * the event's identifiers permit.
 *
 * Polymarket's only working public deep-link form is the slug
 * (`/event/west-virginia-democratic-senate-primary-winner`), NOT the
 * conditionId (`/event/24070494...` 404s). Slug URLs live on the pair
 * / position record upstream; producers thread them into the payload
 * as `payload.polymarketMarketUrl`. We render the Polymarket chip
 * **only** when that slug is present — falling back to a conditionId
 * URL was actively misleading because every conditionId link 404s on
 * click. Better to omit the link than to advertise a broken one.
 * Same contract Opportunities / Positions / Unwinding use
 * (`pair.{site}.marketUrl`).
 */
export function notificationMarketLinks(evt: {
  kalshiTicker?: string | null;
  polyConditionId?: string | null;
  payload?: unknown;
}): Array<{ platform: 'kalshi' | 'polymarket'; url: string }> {
  const out: Array<{ platform: 'kalshi' | 'polymarket'; url: string }> = [];
  const kalshi = kalshiMarketUrlFromTicker(evt.kalshiTicker);
  if (kalshi) out.push({ platform: 'kalshi', url: kalshi });

  const polySlugUrl =
    typeof evt.payload === 'object' &&
    evt.payload !== null &&
    typeof (evt.payload as Record<string, unknown>).polymarketMarketUrl ===
      'string'
      ? ((evt.payload as Record<string, unknown>).polymarketMarketUrl as string)
      : null;
  if (polySlugUrl) out.push({ platform: 'polymarket', url: polySlugUrl });
  return out;
}

/**
 * Wrap any standalone `kalshi` / `polymarket` token in the summary
 * string with a colored, bold span so the platform pops visually.
 * Case-insensitive in the match, but the rendered text is always
 * canonicalised to a short form: `Kalshi` and `Poly` — the latter
 * because "Polymarket" is the longest token in any row and crowded
 * out the actual fill phrase. The colored span keeps each platform
 * scannable even with the abbreviation.
 */
const PLATFORM_DISPLAY: Record<string, string> = {
  kalshi: 'Kalshi',
  polymarket: 'Poly',
};

export function colorizePlatforms(text: string): ReactNode {
  if (!text) return text;
  const parts = text.split(/\b(kalshi|polymarket)\b/gi);
  return parts.map((part, i) => {
    const key = part.toLowerCase();
    const color = PLATFORM_COLORS[key];
    if (!color) return <Fragment key={i}>{part}</Fragment>;
    return (
      <Box
        key={i}
        component="span"
        sx={{ color, fontWeight: 600 }}
      >
        {PLATFORM_DISPLAY[key] ?? part}
      </Box>
    );
  });
}

/**
 * The `SweepStopReason` closed vocabulary — the ONLY valid EXIT reasons
 * on a `mode_change` card. Mirrors the Rust enum tag list (shared so the
 * two agree). Used to distinguish a rising-leg-first EXIT card from a
 * legacy free-form `reason`.
 */
export const SWEEP_STOP_REASONS: ReadonlySet<string> = new Set([
  'sweep_stopped',
  'edge_gone',
  'budget_exhausted',
  'naked_remainder',
  'hedge_failure',
  'maxv_reached',
]);

/**
 * The two EXIT stop reasons that leave the rule with an unhedgeable /
 * naked position. A `mode_change` card carrying one of these is an error,
 * NOT the ordinary `#42a5f5` sweep blue — it renders in the error accent.
 * These are also never FE-collapsed (mirrors the backend dedup, which
 * never suppresses them). `maxv_reached` is a warning, not collapsed.
 */
export const MODE_CHANGE_ERROR_REASONS: ReadonlySet<string> = new Set([
  'naked_remainder',
  'hedge_failure',
]);

/** Error accent — same red the `error` display entry uses. */
const MODE_CHANGE_ERROR_COLOR = '#f44336';
/** Reversal accent — amber, a mid-episode role swap (warning, not error). */
const MODE_CHANGE_REVERSAL_COLOR = '#ffa726';
/** EXIT accent — blue-grey, so a STOP reads apart from the blue ENTRY. */
const MODE_CHANGE_EXIT_COLOR = '#78909c';

/**
 * The row accent color for an event. Normally the per-type color from
 * `NOTIFICATION_DISPLAY`, but a `mode_change` is phase-coloured so the operator
 * can tell start from stop at a glance:
 *   - naked / hedge-failure EXIT → error red (unhedgeable position),
 *   - role REVERSAL (`reversed`) → amber,
 *   - EXIT (terminado, leaving sweep mode) → blue-grey,
 *   - ENTRY (activado) + everything else → the sweep-blue base.
 * Exported for tests.
 */
export function resolveNotificationColor(evt: NotificationEvent): string {
  const base = getNotificationDisplay(evt.type).color;
  if (evt.type === 'mode_change') {
    const p = (evt.payload ?? {}) as {
      reason?: unknown;
      fromMode?: unknown;
      toMode?: unknown;
    };
    const reason = (p.reason ?? '').toString();
    const from = (p.fromMode ?? '').toString();
    const to = (p.toMode ?? '').toString();
    if (MODE_CHANGE_ERROR_REASONS.has(reason)) return MODE_CHANGE_ERROR_COLOR;
    // A reversal keeps `toMode: sweep_rising_leg_first`, so check it before EXIT.
    if (reason === 'reversed') return MODE_CHANGE_REVERSAL_COLOR;
    if (from === 'sweep_rising_leg_first' && to !== 'sweep_rising_leg_first') {
      return MODE_CHANGE_EXIT_COLOR;
    }
  }
  return base;
}

/** One rendered feed row plus how many underlying cards it represents. */
export interface CollapsedFeedRow {
  evt: NotificationEvent;
  /**
   * Number of underlying cards this row stands for: consecutive identical
   * `mode_change` cards for the same rule, plus each card's backend
   * `suppressedCount`. `1` for everything that isn't a collapsed run.
   */
  repeatCount: number;
}

function suppressedCountOf(evt: NotificationEvent): number {
  const raw = (evt.payload as { suppressedCount?: unknown } | undefined)
    ?.suppressedCount;
  const n = typeof raw === 'number' ? raw : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Identity key for collapsing a run of identical `mode_change` cards. Two
 * consecutive cards for the SAME rule with the same `(fromMode, toMode,
 * reason, path)` are the same latch event repeated. Returns `null` for
 * anything that must NOT be collapsed: non-`mode_change` cards, and the
 * error reasons (`naked_remainder` / `hedge_failure`) — mirroring the
 * backend dedup, which never suppresses those.
 */
function modeChangeCollapseKey(evt: NotificationEvent): string | null {
  if (evt.type !== 'mode_change') return null;
  const p = (evt.payload || {}) as Record<string, unknown>;
  const reason = (p.reason ?? '').toString();
  if (MODE_CHANGE_ERROR_REASONS.has(reason)) return null;
  return [
    evt.ruleId ?? '',
    (p.fromMode ?? '').toString(),
    (p.toMode ?? '').toString(),
    reason,
    (p.path ?? '').toString(),
  ].join('|');
}

/**
 * Collapse consecutive identical `mode_change` cards for the same rule
 * into one row carrying a `×N` `repeatCount`. This is a VISUAL BACKSTOP
 * for a sweep-latch regression — after the engine latch lands (spec §4.2)
 * the feed should never contain such a run, and every row's `repeatCount`
 * is `1`. `suppressedCount` from the backend is folded into the count so
 * a single deduped card still shows its multiplicity. Pure + exported for
 * tests.
 */
export function collapseModeChangeRuns(
  rows: NotificationEvent[],
): CollapsedFeedRow[] {
  const out: CollapsedFeedRow[] = [];
  for (const evt of rows) {
    const key = modeChangeCollapseKey(evt);
    const selfCount = 1 + suppressedCountOf(evt);
    const prev = out.length > 0 ? out[out.length - 1] : undefined;
    if (key !== null && prev && modeChangeCollapseKey(prev.evt) === key) {
      prev.repeatCount += selfCount;
      continue;
    }
    out.push({ evt, repeatCount: selfCount });
  }
  return out;
}

/**
 * Convert `pairName` / `kalshiTicker` etc. into "Pair name" / "Kalshi
 * ticker" so the detail view reads like prose, not source code.
 * Acronyms stay intact (TNA, USD).
 */
function formatKey(key: string): string {
  // Insert spaces before capital letters, then capitalize first char.
  const spaced = key
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Stringify a primitive value with light formatting:
 *   - numbers stay as-is (no thousands separators yet — payloads
 *     mix prices/qtys/percentages and we'd guess wrong)
 *   - booleans render as `true` / `false`
 *   - dates / ISO timestamps render via `Date#toLocaleString`
 */
function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') {
    // Heuristic: ISO 8601 timestamps render in the operator's locale.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleString();
    }
    return v;
  }
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

/**
 * Per-event-type custom expansion renderer. When a type has its own
 * layout (e.g. `net_contracts_digest` mirrors the Slack digest card —
 * numbered list of pairs with link chips), this returns a ReactNode
 * that the row swaps in instead of the generic key/value flatten.
 *
 * Returns `null` to fall back to `eventDetailRows`. Add a case here
 * before adding fields to the generic flattener — a custom view is
 * almost always more legible than a 30-row table for structured
 * payloads.
 */
function renderCustomExpandedDetail(
  evt: NotificationEvent,
): ReactNode | null {
  if (evt.type === 'net_contracts_digest') {
    return <NetContractsDigestDetail evt={evt} />;
  }
  return null;
}

interface DigestRow {
  pairId?: string;
  pairName?: string;
  kalshiContracts?: number;
  polymarketContracts?: number;
  netDifference?: number;
  kalshiTicker?: string | null;
  polymarketConditionId?: string | null;
}

/**
 * Mirrors the Slack `buildNetContractsDigestMessage` Block Kit card:
 * a header line with the threshold, then a numbered list of pairs
 * where each row reads `pairName — net: N (Kalshi: K, Poly: P)` and
 * gets per-platform link chips when the row carries the identifiers.
 *
 * Operator scan path: chip color tells them which platform contributes
 * the imbalance, the bold pair name lets them eyeball familiar markets,
 * the numeric tail gives them the magnitude. Same shape as the Slack
 * digest so the two surfaces feel like the same artifact.
 */
function NetContractsDigestDetail({
  evt,
}: {
  evt: NotificationEvent;
}): JSX.Element {
  const payload = (evt.payload ?? {}) as Record<string, unknown>;
  const threshold = Number(payload.threshold ?? 0);
  const rows = Array.isArray(payload.top)
    ? (payload.top as DigestRow[])
    : [];

  const fmtInt = (n: number | undefined): string => {
    if (n == null || !Number.isFinite(n)) return '?';
    // Slack digest renders with thousands separators + 3 decimals
    // (see slack-message.builder.ts → fmt). Match that here so the
    // two surfaces line up character-for-character on the same data.
    return n.toLocaleString('en-US', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  return (
    <Stack spacing={1.25}>
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ color: 'common.white', fontWeight: 600 }}
        >
          Hourly Net Contracts Imbalance
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Top {rows.length} pair{rows.length === 1 ? '' : 's'} where absolute
          net contracts difference is at least{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {fmtInt(threshold)}
          </Box>
          .
        </Typography>
      </Box>
      <Stack spacing={0.75}>
        {rows.map((row, idx) => {
          const links = notificationMarketLinks({
            kalshiTicker: row.kalshiTicker ?? undefined,
            polyConditionId: row.polymarketConditionId ?? undefined,
          });
          return (
            <Box key={`${row.pairId ?? idx}-${idx}`}>
              <Typography
                variant="body2"
                sx={{ color: 'common.white', lineHeight: 1.4 }}
              >
                <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                  {idx + 1}.
                </Box>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {row.pairName ?? '_(unknown pair)_'}
                </Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' '}
                  — net:{' '}
                </Box>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {fmtInt(row.netDifference)}
                </Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' '}
                  (Kalshi: {fmtInt(row.kalshiContracts)}, Poly:{' '}
                  {fmtInt(row.polymarketContracts)})
                </Box>
              </Typography>
              {links.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ mt: 0.25, ml: 2.5 }}
                  alignItems="center"
                >
                  {links.map((link) => (
                    <Chip
                      key={link.platform}
                      component="a"
                      clickable
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      size="small"
                      label={link.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'}
                      variant="outlined"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        borderColor: PLATFORM_COLORS[link.platform],
                        color: PLATFORM_COLORS[link.platform],
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}

/**
 * Walk the event into a flat list of `[label, value]` rows for the
 * expanded detail view. Top-level fields come first, then payload
 * fields (recursed up to 2 levels deep — anything deeper falls
 * back to a JSON-ish summary so the row stays single-line).
 *
 * Skips null/undefined and the redundant `payload` wrapper key —
 * its contents are merged inline with parent breadcrumb prefixes
 * so the operator doesn't need to mentally unwrap nested objects.
 */
/**
 * Human-readable description of the fill subtype for the expanded
 * details. Many fill event types collapse into the same row chip
 * ("filled" / "partial"), so the operator needs a clear in-panel
 * answer to "what kind of fill triggered this row?". Returns `null`
 * for non-fill events — the row's existing `Type` line is plenty.
 *
 * `recovery_filled` is special-cased on `counterAction`: when the
 * counter is `executing`, the recovery is still in-flight (engine is
 * hedging the leg automatically); `failed` / `no_bid` means the
 * operator must hedge manually. The label reflects that since it
 * changes what the operator should do next.
 */
function describeFillKind(evt: NotificationEvent): string | null {
  const p = (evt.payload || {}) as Record<string, unknown>;
  const counterAction = p.counterAction;
  switch (evt.type) {
    case 'maker_filled':
      return 'Maker fill (full) — resting order fully executed';
    case 'counter_filled':
      return 'Counter fill (full) — counter leg fully executed';
    case 'crossing_executed':
      return 'Crossing fill (full) — crossed the spread, fully executed';
    case 'maker_partial_fill':
      return 'Maker fill (partial) — resting order partially executed';
    case 'partial_fill_accumulated':
      return 'Maker fill (partial, accumulated) — partial chunk added to running total before counter trigger';
    case 'crossing_partial':
      return 'Crossing fill (partial) — crossing order partially executed';
    case 'crossing_accumulated':
      return 'Crossing fill (partial, accumulated) — crossing chunk added to running total, still below counter min';
    case 'recovery_filled':
      if (counterAction === 'executing') {
        return 'Recovery fill — automation is hedging the leg';
      }
      if (counterAction === 'no_bid') {
        return 'Recovery fill — no counter bid, manual hedge required';
      }
      if (counterAction === 'failed') {
        return 'Recovery fill — counter failed, manual hedge required';
      }
      return 'Recovery fill';
    case 'manual_order_executed':
      return 'Manual order fill — order placed by operator filled';
    case 'manual_order_partial_fill':
      return 'Manual order partial fill — chunk matched, order still resting';
    default:
      return null;
  }
}

function eventDetailRows(evt: NotificationEvent): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  rows.push(['Id', evt.id]);
  rows.push(['Type', evt.type]);
  // `Fill kind` is the in-panel companion to the collapsed row chip:
  // many fill subtypes share the same "filled" / "partial" chip, so
  // the operator needs a human-readable answer here for "what
  // exactly triggered this fill?". Placed right after `Type` so it
  // sits at the top of the details, before the payload dump.
  const fillKind = describeFillKind(evt);
  if (fillKind) rows.push(['Fill kind', fillKind]);
  rows.push(['Severity', evt.severity]);
  rows.push(['Source', evt.source]);
  rows.push(['Occurred at', formatValue(evt.occurredAt)]);
  if (evt.ruleId) rows.push(['Rule id', evt.ruleId]);
  if (evt.pairId) rows.push(['Pair id', evt.pairId]);
  if (evt.pairName) rows.push(['Pair name', evt.pairName]);
  if (evt.platform) rows.push(['Platform', String(evt.platform)]);
  if (evt.kalshiTicker) rows.push(['Kalshi ticker', evt.kalshiTicker]);
  if (evt.polyConditionId)
    rows.push(['Polymarket condition id', evt.polyConditionId]);

  const payload = evt.payload as Record<string, unknown> | undefined;
  if (payload && typeof payload === 'object') {
    rows.push(...flattenPayload(payload, ''));
  }
  return rows;
}

function flattenPayload(
  obj: Record<string, unknown>,
  prefix: string,
  depth = 0,
): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v == null) continue;
    const label = prefix ? `${prefix} → ${formatKey(k)}` : formatKey(k);
    if (Array.isArray(v)) {
      // Arrays of objects (e.g. the digest's `top` list of pairs)
      // get expanded entry-by-entry so the operator sees each
      // pair's fields inline. Without this the digest detail view
      // showed only "Top: 5 items" — the per-pair contract counts
      // were buried in `payload.top` and never surfaced. Arrays of
      // primitives keep the compact "N items" summary since
      // expanding them rarely adds signal.
      const isObjectArray =
        v.length > 0 &&
        typeof v[0] === 'object' &&
        v[0] !== null &&
        !Array.isArray(v[0]);
      if (isObjectArray && depth < 2) {
        v.forEach((item, idx) => {
          out.push(
            ...flattenPayload(
              item as Record<string, unknown>,
              `${label} [${idx + 1}]`,
              depth + 1,
            ),
          );
        });
      } else {
        out.push([label, `${v.length} item${v.length === 1 ? '' : 's'}`]);
      }
    } else if (typeof v === 'object' && depth < 2) {
      out.push(
        ...flattenPayload(v as Record<string, unknown>, label, depth + 1),
      );
    } else if (typeof v === 'object') {
      // Past the recursion budget — surface the keys so the
      // operator at least knows the shape, without dumping the
      // tree.
      const keys = Object.keys(v as object).join(', ');
      out.push([label, keys ? `{ ${keys} }` : '{}']);
    } else {
      out.push([label, formatValue(v)]);
    }
  }
  return out;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// Order-lifecycle noise: the engine constantly re-prices, places, and
// cancels the resting maker, so amends / placements / cancellations are
// high-frequency and low-value for the operator. Book health events
// (`book_stale` / `book_fresh`) are the same kind of high-frequency
// churn. They're hidden from the feed by DEFAULT (kept only briefly in
// Mongo, see notifications-core `retention.ts`). The "Show order
// lifecycle" switch flips them back on via the URL param.
// `maker_cancelled` is hidden for ALL reasons here — not reason-level —
// and `counter_placed` is intentionally NOT included (the operator wants
// to keep seeing counter placements).
const LIFECYCLE_NOISE_TYPES: NotificationEventType[] = [
  'maker_adjusted',
  'maker_placed',
  'maker_cancelled',
  'book_stale',
  'book_fresh',
  // Sweep mode toggles (`Sweep …activado/desactivado`) are engine-driven
  // lifecycle churn, same as maker placements.
  'mode_change',
  // The over-hedge withhold fires on every book-cross verification, so it
  // repeats at maker-repricing frequency. Nothing for the operator to do —
  // the engine already protected the money.
  'book_cross_saved',
  // Orphan detection rides the ~20s belief-reconcile probe, so on a busy leg
  // it arrives at maker-repricing frequency too: 217 rows in the first 8
  // minutes it was live in production (2026-08-22), all Polymarket. The engine
  // side of that flood is fixed — the card now waits until the order is
  // provably superseded rather than firing on every reprice — but the row it
  // leaves behind is still engine bookkeeping, and with the cancel armed the
  // engine has already dealt with it. The card that DOES need the operator is
  // `stale_resting_order_cancel_resolved`, which stays in the main feed.
  'stale_resting_order_detected',
];

export function readFilterFromParams(
  params: URLSearchParams,
): NotificationsFilter {
  const types = params.getAll('type') as NotificationEventType[];
  // Default: lifecycle noise hidden. `showLifecycle=1` reveals it. The
  // legacy `showAmends=1` param is still honored so old bookmarks keep
  // working after the rename.
  const showLifecycle =
    params.get('showLifecycle') === '1' || params.get('showAmends') === '1';
  return {
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    ruleId: params.get('ruleId') ?? undefined,
    pairId: params.get('pairId') ?? undefined,
    types: types.length > 0 ? types : undefined,
    excludeTypes: showLifecycle ? undefined : LIFECYCLE_NOISE_TYPES,
    direction:
      (params.get('direction') as NotificationsFilter['direction']) ?? 'all',
    platform: (params.get('platform') as NotificationsFilter['platform']) ?? 'all',
    source: (params.get('source') as NotificationsFilter['source']) ?? 'all',
    severity:
      (params.get('severity') as NotificationsFilter['severity']) ?? 'all',
  };
}

export function writeFilterToParams(
  filter: NotificationsFilter,
): URLSearchParams {
  const p = new URLSearchParams();
  if (filter.from) p.set('from', filter.from);
  if (filter.to) p.set('to', filter.to);
  if (filter.ruleId) p.set('ruleId', filter.ruleId);
  if (filter.pairId) p.set('pairId', filter.pairId);
  if (filter.types) for (const t of filter.types) p.append('type', t);
  if (filter.direction && filter.direction !== 'all')
    p.set('direction', filter.direction);
  if (filter.platform && filter.platform !== 'all')
    p.set('platform', filter.platform);
  if (filter.source && filter.source !== 'all') p.set('source', filter.source);
  if (filter.severity && filter.severity !== 'all')
    p.set('severity', filter.severity);
  // Persist "show order lifecycle" only when ON. `excludeTypes` unset =
  // lifecycle noise shown.
  if (!filter.excludeTypes || filter.excludeTypes.length === 0) {
    p.set('showLifecycle', '1');
  }
  return p;
}

/**
 * Renders `rule_started`'s `payload.paths` (lowercase `'a'`/`'b'`, A
 * before B) as a human-readable arb-path segment. Path A = Poly YES +
 * Kalshi NO; path B = Poly NO + Kalshi YES. Legacy events without the
 * field (or with an empty/malformed value) get an empty string so the
 * segment is dropped by the `.filter(Boolean)` join.
 */
function formatRulePaths(paths: unknown): string {
  if (!Array.isArray(paths)) return '';
  const hasA = paths.includes('a');
  const hasB = paths.includes('b');
  if (hasA && hasB) return 'paths A+B';
  if (hasA) return 'path A (Poly YES + Kalshi NO)';
  if (hasB) return 'path B (Poly NO + Kalshi YES)';
  return '';
}

/**
 * How a cancel's leftovers may be stated. Three outcomes, and the middle one is
 * the whole point: a cancel that could not be CONFIRMED never gets to assert its
 * numbers, however small they are. `remaining: 0` off an unverified sweep is a
 * read nobody stood behind — a budget-exhausted loop reports the last count it
 * happened to see — and "0 and 0" is exactly what an operator reads as "nothing
 * left". Print the numbers, refuse the verdict.
 */
function describeCancelLeftovers(p: Record<string, any>): string | null {
  const show = (v: unknown): string => (v == null ? 'UNKNOWN' : String(v));
  const counts = `${show(p.kalshiRemaining)} Kalshi / ${show(p.polymarketRemaining)} Poly`;
  if (p.cancelUnverified === true) {
    return `⚠ cancel NOT confirmed — last read: ${counts}`;
  }
  const left = [p.kalshiRemaining, p.polymarketRemaining].some(
    (v) => v == null || Number(v) > 0,
  );
  return left ? `⚠ still resting: ${counts}` : null;
}

/**
 * Pulls the most operator-relevant fields out of the type-specific
 * payload to render a one-line plain-text summary on each row. The
 * row's expanded panel renders the full event as a key/value list,
 * so this string only needs the "what just happened" gist.
 *
 * Every `EventType` has an explicit case — the default branch is a
 * defensive fallback for an unknown type rolled out by the backend
 * before this map was updated. It still avoids the raw-JSON dump
 * by walking the payload's own keys.
 */
export function summarizeEvent(evt: NotificationEvent): string {
  const p = (evt.payload || {}) as any;
  const platform = (p.platform ?? evt.platform ?? '').toString();
  const pair = p.pairName ?? evt.pairName ?? '';
  const sep = ' · ';
  // Render a price as "$0.85" — round off float noise (a backend
  // `0.31999999999999995` must not leak to the row) while keeping up to
  // 4 decimals for sub-cent Polymarket ticks, trailing zeros trimmed.
  const dollars = (v: unknown): string => {
    if (v == null || v === '') return '?';
    const n = Number(v);
    if (!Number.isFinite(n)) return '?';
    return `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
  };
  // Polymarket ids are `0x` + 64 hex. Rendered whole they eat the row's
  // whole width and push the numbers that matter off the end, so show
  // enough to match against the venue and no more.
  const shortId = (v: string): string =>
    v.length > 14 ? `${v.slice(0, 8)}…${v.slice(-4)}` : v;

  switch (evt.type) {
    // ─── Order lifecycle ────────────────────────────────────────────
    // One base case per kind covers BOTH directions — payload shapes
    // are identical, and `payload.action` ('buy' on opener, 'sell' on
    // unwinder) drives the verb in `fillPhrase`. Where the entry vs
    // unwind wording genuinely differs (cycle done, rule bookends,
    // below-minimum) the case branches on `evt.direction`.
    case 'maker_placed':
      return [platform, pair, `${p.side?.toUpperCase?.() ?? ''} ${p.qty ?? '?'}@${dollars(p.price)}`]
        .filter(Boolean)
        .join(sep);
    case 'maker_adjusted': {
      // Show whichever field(s) actually changed plus the resulting
      // position so the operator can see "what just moved" AND
      // "where the order is now" in one glance. Without the
      // resulting-state fallback, an amend that only changed qty
      // rendered as "$0.58 → $0.58" — accurate but useless. The
      // direction arrow ` → ` is reserved for the field that moved.
      const priceChanged = p.oldPrice !== p.newPrice;
      const qtyChanged = p.oldQty != null && p.oldQty !== p.newQty;
      const priceText = priceChanged
        ? `${dollars(p.oldPrice)} → ${dollars(p.newPrice)}`
        : dollars(p.newPrice);
      const qtyText =
        p.newQty == null
          ? ''
          : qtyChanged
            ? `qty ${p.oldQty} → ${p.newQty}`
            : `qty ${p.newQty}`;
      return [platform, pair, priceText, qtyText]
        .filter(Boolean)
        .join(sep);
    }
    case 'maker_cancelled': {
      // `reason: 'panic'` flows from the operator-initiated PANIC sweep
      // (`OrderManager.panicCancelMakers`). Surface it as a clear
      // operator-intent label so the row doesn't read like an engine
      // error. All other reasons render verbatim.
      const reasonText =
        p.reason === 'panic'
          ? 'by panic stop'
          : p.reason ?? '';
      return [platform, pair, reasonText].filter(Boolean).join(sep);
    }
    case 'maker_partial_fill':
      // Kalshi-email-style fill phrase. `side` is missing on legacy
      // payloads (older builds didn't thread it through); fall back
      // to "?" so the rest of the line still reads naturally.
      return [
        platformWithSide(platform, p.side),
        pair,
        partialFillPhrase(p.filledQty, p.totalQty, p.side, p.filledPrice, p.action),
      ]
        .filter(Boolean)
        .join(sep);
    case 'partial_fill_accumulated':
      // The delta is the chunk that just filled; the running total
      // is shown in the parenthetical so the operator sees both
      // "what just landed" and "how close are we to the counter min".
      return [
        platformWithSide(platform, p.side),
        pair,
        `${fillPhrase(p.deltaQty, p.side, p.filledPrice ?? p.fillPrice, p.action)} ` +
          `(accumulated ${p.accumulatedQty ?? '?'}/${p.totalQty ?? '?'})`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'maker_filled':
      // `action` ('buy' on the opener, 'sell' on the unwinder) drives the
      // Bought/Sold verb; without it `actionVerb` defaults to "Sold" and an
      // entry fill read "Sold 50 shares…". The engine sends canonical
      // `filledQty`/`filledPrice` (was `fillSize`/`fillPrice`, which rendered
      // the whole phrase as "Sold ? shares of Yes at ?").
      return [
        platformWithSide(platform, p.side),
        pair,
        fillPhrase(p.filledQty, p.side, p.filledPrice, p.action),
        p.tna != null
          ? `TNA ${Number(p.tna).toFixed?.(2) ?? p.tna}%`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_placed':
      // Unwinder emits `payload.counterPlatform`; opener doesn't (its
      // payload is `{price, size, orderId}` and the counter platform
      // lives in the top-level `evt.platform` set via the event ctx).
      // Fall back through the destructured `platform` so opener rows
      // surface the platform too. Opener payload also uses `size`
      // instead of `qty`, and has no `orderType` for counter — show a
      // neutral verb instead of an empty prefix.
      return [
        p.counterPlatform ?? platform,
        pair,
        `${p.orderType?.toUpperCase?.() ?? 'COUNTER'} ${p.qty ?? p.size ?? '?'}@${dollars(p.price)}`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_filled':
      return [
        platformWithSide(p.counterPlatform ?? platform, p.side),
        pair,
        `${p.orderType?.toUpperCase?.() ?? 'COUNTER'} filled ${p.filledQty ?? '?'}@${dollars(p.filledPrice)}`,
        typeof p.fillLatencyMs === 'number' ? `${p.fillLatencyMs}ms` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'crossing_executed':
      // `p.action` ('buy'/'sell') MUST be passed or `actionVerb` defaults to
      // "Sold" — an opener crossing then read "Sold ? shares…". Quantity falls
      // back through qty → filledQty → size (the engine's crossing payload uses
      // `size`, while sibling fill events use `filledQty`).
      return [
        platformWithSide(platform, p.side),
        pair,
        fillPhrase(
          p.qty ?? p.filledQty ?? p.size,
          p.side,
          p.price ?? p.filledPrice,
          p.action,
        ),
      ]
        .filter(Boolean)
        .join(sep);
    case 'crossing_partial':
      return [
        platformWithSide(platform, p.side),
        pair,
        partialFillPhrase(p.filledQty, p.totalQty, p.side, p.filledPrice),
      ]
        .filter(Boolean)
        .join(sep);
    case 'crossing_accumulated':
      return [
        platformWithSide(platform, p.side),
        pair,
        `${fillPhrase(p.filledQty, p.side, p.filledPrice)} (accumulated, below counter min)`,
      ]
        .filter(Boolean)
        .join(sep);
    // ─── Manual orders ───────────────────────────────────────────────
    case 'manual_order_placed':
      // Placement is pre-fill — show "Placing X to sell..." as a
      // present-progressive phrase. Action+side come from payload.
      return [
        platform,
        p.marketTitle ?? '',
        `${actionVerb(p.action) === 'Bought' ? 'Buying' : 'Selling'} ${p.qty ?? '?'} shares of ${sideTitle(p.side)} at ${centsFmt(p.price ?? null)}`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'manual_order_executed':
      return [
        platformWithSide(platform, p.side),
        p.marketTitle ?? '',
        fillPhrase(p.filledQty, p.side, p.price, p.action),
      ]
        .filter(Boolean)
        .join(sep);
    case 'manual_order_partial_fill':
      return [
        platformWithSide(platform, p.side),
        p.marketTitle ?? '',
        partialFillPhrase(p.filledQty, p.totalQty, p.side, p.price, p.action),
      ]
        .filter(Boolean)
        .join(sep);
    // ─── UI + Slack ──────────────────────────────────────────────────
    case 'execution_complete':
      // Cycle done. The entry (opener) and unwind payloads differ:
      //   - unwind: TNA + capitalRecovered are the headline numbers
      //   - entry:  sumGross / contracts / per-leg prices (both legs
      //     filled below the hard $1 cap)
      // Branch on `evt.direction` so each side reads the way the
      // operator expects; "$cost · N contracts · sum 0.94" for entry.
      if (evt.direction === 'entry') {
        return [
          pair,
          p.contracts != null ? `${p.contracts} contracts` : '',
          p.sumGross != null ? `sum ${Number(p.sumGross).toFixed(3)}` : '',
          p.makerPrice != null && p.counterPrice != null
            ? `${dollars(p.makerPrice)}+${dollars(p.counterPrice)}`
            : '',
        ]
          .filter(Boolean)
          .join(sep);
      }
      return [
        pair,
        p.effectiveTna != null
          ? `TNA ${Number(p.effectiveTna).toFixed(2)}%`
          : '',
        p.capitalRecovered != null
          ? `capital ${dollars(p.capitalRecovered)}`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'error': {
      // Opener publishes `message`; legacy unwinder payloads use
      // `errorMessage`. The opener also attaches `attemptedOrder`
      // (price/qty/platform) on placement-error paths so the operator
      // sees the order we were trying to put when triaging the row.
      const errMsg = p.message ?? p.errorMessage ?? '';
      const ao = p.attemptedOrder as
        | { price?: number; qty?: number; platform?: string }
        | undefined;
      const attempted =
        ao && (ao.price != null || ao.qty != null)
          ? `try ${ao.qty ?? '?'}@${dollars(ao.price)}${
              ao.platform ? ` on ${ao.platform}` : ''
            }`
          : '';
      return [pair || evt.ruleId || '', p.state ?? '', attempted, errMsg]
        .filter(Boolean)
        .join(sep);
    }
    case 'counter_failed':
      return [
        pair,
        p.counterPlatform ?? '',
        p.reason ?? '',
        p.errorMessage ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_stuck':
      // The counter maker is resting unfilled; the engine alerts but leaves
      // it in place. Surface how long it's been stuck + the un-hedged size.
      // `platform` here is the COUNTER (hedge) venue where the order rests
      // (see `emit_counter_maker_stuck`'s doc comment), so tagging it with
      // `counterSide` shows exactly which side is the stuck one.
      return [
        platformWithSide(platform, p.counterSide),
        pair,
        p.restingSecs != null ? `resting ${p.restingSecs}s` : '',
        p.unhedgedQty != null ? `${p.unhedgedQty} un-hedged` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_recovered':
      // A counter whose create-ACK was lost was reconciled by COID and
      // re-adopted as the engine's own resting order — recovery/good news.
      return [
        platform,
        pair,
        `counter recovered ${p.qty ?? '?'}@${dollars(p.price)}`,
        p.path ? `path ${p.path}` : '',
        p.market ? `(${p.market})` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_rearmed':
      // A 0-fill cancelled counter was re-placed at a still-reachable price.
      return [
        platform,
        pair,
        `counter re-armed ${p.qty ?? '?'}@${dollars(p.price)}`,
        p.path ? `path ${p.path}` : '',
        p.market ? `(${p.market})` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_unconfirmed':
      // A counter could NOT be confirmed at the venue (429 storm) — the
      // operator must check the position manually.
      return [
        platform,
        pair,
        `counter UNCONFIRMED — check manually ${p.qty ?? '?'}@${dollars(p.price)}`,
        p.coid ? `coid ${p.coid}` : '',
        p.market ? `(${p.market})` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_capped_resting':
      // A stuck counter-hedge escalation declined to cross the book above the
      // rule's threshold / max-sum cap, so it left the remainder resting as a
      // GTC maker instead of taking. Surface the resting size + the cap price
      // it held at (and the cap mode / sum for context).
      return [
        platform,
        pair,
        `counter capped ${p.restingQty ?? '?'}@${dollars(p.capPrice)} resting`,
        p.mode ? `(${p.mode})` : '',
        p.capSum != null ? `would breach ${p.capSum} sum` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'out_of_balance': {
      // Polymarket returns balance / order amount in raw 6-decimal token
      // units (1 contract = 1,000,000 raw); Kalshi returns plain
      // contract counts. Render both as "X contracts (Y raw)" when the
      // platform is Polymarket so the operator doesn't have to mental-
      // divide by 1e6. The expanded panel still shows the raw payload
      // values verbatim for debugging.
      const isPoly = String(platform).toLowerCase().startsWith('poly');
      const fmt = (raw: unknown): string => {
        const n = Number(raw);
        if (!Number.isFinite(n)) return String(raw);
        if (isPoly) {
          const contracts = n / 1_000_000;
          // Show fractional only when below 1 contract; whole-contracts
          // otherwise — typical orders are dozens to hundreds of contracts.
          const display =
            Math.abs(contracts) < 1
              ? contracts.toFixed(4).replace(/\.?0+$/, '')
              : Math.round(contracts).toString();
          return `${display} contracts`;
        }
        return `${n} contracts`;
      };
      const detail = [
        p.balance != null ? `bal ${fmt(p.balance)}` : '',
        p.orderAmount != null ? `req ${fmt(p.orderAmount)}` : '',
        p.committed != null && Number(p.committed) > 0
          ? `committed ${fmt(p.committed)}`
          : '',
      ]
        .filter(Boolean)
        .join(', ');
      return [platform, pair, p.reason ?? '', detail]
        .filter(Boolean)
        .join(sep);
    }
    case 'rule_below_minimum': {
      // Entry (opener) uses `qty`/`minimum`/`price`/`notional`/`reason`;
      // unwind uses `computedQty`/`computedPrice`/`minQty`. Reading both
      // and preferring the present field is direction-agnostic, so a
      // single base case handles both sides. `reason` discriminates the
      // gate that tripped on
      // Polymarket — 'notional_floor' (size>=5 OK but size*price<$1) needs
      // a different summary than 'size_floor' or the row reads as a
      // contradiction (e.g. "qty 16 < min 5").
      const qty = p.qty ?? p.computedQty;
      const price = p.price ?? p.computedPrice;
      const minQ = p.minimum ?? p.minQty;
      const notional = p.notional ?? (qty != null && price != null ? Number(qty) * Number(price) : null);
      const reason = p.reason as 'size_floor' | 'notional_floor' | undefined;
      const inferred =
        reason ??
        (qty != null && minQ != null && Number(qty) < Number(minQ)
          ? 'size_floor'
          : notional != null && Number(notional) < 1
            ? 'notional_floor'
            : 'size_floor');
      const detail =
        inferred === 'notional_floor'
          ? `${qty ?? '?'}@${dollars(price)} → notional $${(notional ?? 0).toFixed(2)} < $1 min`
          : `${qty ?? '?'}@${dollars(price)} (min ${minQ ?? '?'} contracts)`;
      return [platform, pair, detail].filter(Boolean).join(sep);
    }
    case 'max_volume_reached': {
      const exec = Number(p.totalVolumeExecutedContracts ?? 0);
      const cap = Number(p.maxVolumeContracts ?? 0);
      return [pair, `${exec}/${cap} contracts — engine halted`]
        .filter(Boolean)
        .join(sep);
    }
    case 'dust_remaining':
      return [platform, pair, `dust ${p.dustQty ?? '?'}`]
        .filter(Boolean)
        .join(sep);
    case 'market_takeover':
      return [
        platform,
        pair,
        `${p.cancelledCount ?? 0} order(s) cancelled`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'tna_negative':
      return [
        pair,
        p.takerTna != null
          ? `taker TNA ${Number(p.takerTna).toFixed(2)}%`
          : 'taker TNA negative',
      ]
        .filter(Boolean)
        .join(sep);
    case 'net_contracts_digest': {
      // Surface the first few pair names + their net imbalance inline
      // so the operator can scan the digest without expanding it. The
      // row truncates with ellipsis if the list runs long.
      const top = Array.isArray(p.top) ? p.top : [];
      const head = top
        .slice(0, 3)
        .map((t: any) => {
          const n = Number(t?.netDifference);
          const sign =
            Number.isFinite(n) && n > 0 ? '+' : '';
          return `${t?.pairName ?? '?'} ${sign}${t?.netDifference ?? '?'}`;
        })
        .join(', ');
      const rest = top.length > 3 ? `, +${top.length - 3} more` : '';
      const tail = head ? `${head}${rest}` : `${top.length} pair(s)`;
      return `digest threshold $${p.threshold ?? '?'} · ${tail}`;
    }
    case 'emergency_stop': {
      const head = `${p.rulesAffected ?? p.rulesPaused ?? '?'} rule(s) cancelled`;
      const detail = p.detail ?? p.reason;
      return detail ? `${head}${sep}${detail}` : head;
    }
    case 'emergency_resume':
      return `${p.rulesResumed ?? '?'} rule(s) resumed`;
    case 'recovery_filled':
      return [
        platformWithSide(p.makerPlatform ?? platform, p.side),
        pair,
        fillPhrase(p.filledQty, p.side, p.filledPrice),
        `counter ${p.counterAction ?? ''}`,
        p.errorMessage ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'recovery_restored':
      return [
        platform,
        pair,
        `restored ${p.qty ?? '?'} shares of ${sideTitle(p.side)} at ${centsFmt(p.price ?? null)}`,
        p.reason ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_skipped':
      return [
        pair,
        p.reason ?? '',
        p.rawBid != null ? `rawBid ${p.rawBid}` : '',
        p.netBid != null ? `netBid ${p.netBid}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'platform_disconnect':
      return [
        p.platform ?? platform,
        p.wsType ?? '',
        `${p.cancelledCount ?? 0} cancelled`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'platform_reconnect':
      return `${p.platform ?? platform} reconnected`;
    case 'rule_started': {
      // Trader-feed bookend. The entry (opener) and unwind payloads
      // differ, so branch on `evt.direction`:
      //   - entry: no contracts-on-platform yet (we're opening) — surface
      //     the target TNA / threshold the rule was activated with.
      //   - unwind: an `activationSnapshot` of open contracts per platform;
      //     surface any mismatch so the operator can reconcile. Falls back
      //     to a shape-of-rule one-liner on legacy events that predate the
      //     snapshot field.
      if (evt.direction === 'entry') {
        return [
          pair,
          formatRulePaths(p.paths),
          Array.isArray(p.platforms) ? p.platforms.join('+') : '',
          p.targetTnaPercent != null ? `target TNA ${p.targetTnaPercent}%` : '',
          p.thresholdSum != null ? `threshold ${p.thresholdSum}` : '',
        ]
          .filter(Boolean)
          .join(sep);
      }
      const snap = p.activationSnapshot;
      if (snap) {
        const k = snap.kalshiContracts ?? 0;
        const m = snap.polymarketContracts ?? 0;
        const diff = snap.mismatchContracts ?? 0;
        const head = `Kalshi ${k} · Poly ${m}`;
        const tail =
          diff > 0 ? `mismatch ${diff} contracts` : 'positions matched';
        return [pair, formatRulePaths(p.paths), head, tail]
          .filter(Boolean)
          .join(sep);
      }
      const platformsList = Array.isArray(p.platforms)
        ? p.platforms.join('+')
        : '';
      return [
        pair,
        formatRulePaths(p.paths),
        platformsList,
        `target ${p.contractsToSell ?? '?'}`,
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'rule_completed':
      // Entry (opener) reports cycles + total contracts + reason; unwind
      // has no distinct payload today and falls through to the same
      // best-effort fields.
      return [
        pair,
        p.cyclesCompleted != null ? `${p.cyclesCompleted} cycle(s)` : '',
        p.totalContracts != null ? `${p.totalContracts} contracts` : '',
        p.reason ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'rule_paused':
      return [pair || evt.ruleId || '', p.reason ?? 'paused']
        .filter(Boolean)
        .join(sep);
    case 'rule_cancelled':
      return [pair || evt.ruleId || '', p.reason ?? 'cancelled']
        .filter(Boolean)
        .join(sep);
    case 'constraint_violated':
      // Hard $1 cap tripped — the single most important opener event.
      // Keep the line dense: sum / cap / per-leg prices so the
      // operator can immediately see how far over the cap we were.
      // The rule is auto-paused; this notification is the call-to-arms.
      return [
        pair || evt.ruleId || '',
        p.sumGross != null
          ? `sum ${Number(p.sumGross).toFixed(4)}`
          : '',
        p.cap != null ? `cap ${Number(p.cap).toFixed(4)}` : '',
        p.makerPriceGross != null
          ? `maker ${dollars(p.makerPriceGross)}`
          : '',
        p.counterTopAsk != null
          ? `counter ask ${dollars(p.counterTopAsk)}`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'maker_yielded_to_counter':
      // The maker yielded its resting position because the counter leg
      // filled first — surface how many contracts moved to the counter.
      return [
        platform,
        pair,
        p.yieldedQty != null ? `${p.yieldedQty} yielded to counter` : 'yielded to counter',
      ]
        .filter(Boolean)
        .join(sep);
    case 'book_stale':
      return [
        p.platform ?? platform,
        // The engine always sends a non-empty `subject` (pair name when the
        // token resolves to a loaded rule, else the raw market id), so a book
        // alarm never renders with no market.
        pair || p.subject || p.market || '',
        p.staleSecs != null ? `stale ${p.staleSecs}s` : 'orderbook stale',
        p.cancelledCount != null ? `${p.cancelledCount} cancelled` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'book_fresh':
      return [
        p.platform ?? platform,
        pair || p.subject || p.market || '',
        p.staleSecs != null ? `recovered after ${p.staleSecs}s` : 'orderbook fresh',
      ]
        .filter(Boolean)
        .join(sep);
    case 'over_buy':
    case 'over_sell': {
      // Position-balance fault: the engine moved more on one leg than the
      // other. Surface the overshoot magnitude + the leg it landed on.
      const verb = evt.type === 'over_buy' ? 'over-bought' : 'over-sold';
      return [
        platform,
        pair,
        p.overshootQty != null ? `${verb} ${p.overshootQty}` : verb,
        p.reason ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'leg_imbalance':
      return [
        pair,
        p.kalshiContracts != null && p.polymarketContracts != null
          ? `Kalshi ${p.kalshiContracts} · Poly ${p.polymarketContracts}`
          : '',
        p.imbalanceQty != null ? `imbalance ${p.imbalanceQty}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'engine_live_task_panic':
      return [pair || evt.ruleId || '', p.message ?? p.errorMessage ?? 'live task panic']
        .filter(Boolean)
        .join(sep);
    case 'order_rejected_rate_limited':
      return [
        platform,
        pair || evt.ruleId || '',
        p.action ?? '',
        p.retryAfterMs != null ? `retry ${p.retryAfterMs}ms` : 'rate limited',
      ]
        .filter(Boolean)
        .join(sep);
    case 'order_rate_limit_waiting':
      // `p.bucket` is the whole point of the card — each budget has its own
      // cause. Kalshi: `write` / `cancel` / `read`. Polymarket: `order` /
      // `cancel` / `batch` / `read`. `read` is REST polling and is emitted for
      // BOTH platforms, so `kalshi · read budget` is a real row. Without the
      // bucket the row says a budget is throttling but not which one.
      //
      // Rendered as "<bucket> budget", matching the Rust title ("kalshi write
      // budget limited"): bare `order` / `cancel` sit next to the action slot
      // and read as operations the engine PERFORMED, which is what `cancel`
      // means everywhere else in this feed.
      //
      // The overdraft, not the milliseconds, is the depth of the episode: a
      // wait means nothing without the bucket's refill rate, which spans 50ms
      // to 5.6ms across buckets. `overdrawnTokens` is how many callers the
      // budget is behind. Trailing zeros dropped so a marginal 0.02 doesn't
      // render as "0.0", which would read as no overdraft at all.
      return [
        platform,
        p.bucket ? `${p.bucket} budget` : '',
        pair || evt.ruleId || '',
        p.action ?? '',
        p.waitMs != null ? `waiting ${p.waitMs}ms` : 'rate-limit wait',
        typeof p.overdrawnTokens === 'number' && p.overdrawnTokens > 0
          ? `overdrawn ${Number(p.overdrawnTokens.toFixed(2))}`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'hedge_deferred':
      // A maker partial couldn't be taker-hedged under the hard $1 cap;
      // the engine rested a passive counter maker instead of abandoning
      // the contracts.
      return [
        platform,
        pair,
        p.deferredQty != null ? `${p.deferredQty} deferred to maker` : 'hedge deferred',
        p.price != null ? `@${dollars(p.price)}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'mode_change': {
      // The opener/unwinder sweep state machine flipped operating mode.
      // Render one glanceable Spanish line per phase of the rising-leg-first
      // sweep: ENTRY (activado), REVERSAL (revertido), EXIT (terminado). The
      // legacy `sweep_dual_post` mode and the pure `from → to` fallback are
      // kept so historical / unrecognized cards keep rendering (and a payload
      // that lacks the new keys never crashes — spec §5.22).
      const from = (p.fromMode ?? '').toString();
      const to = (p.toMode ?? '').toString();
      const reason = (p.reason ?? '').toString();
      const path = (p.path ?? '').toString();
      const pathSuffix = (extra?: string) => {
        const inner = [path && `path ${path}`, extra].filter(Boolean).join(', ');
        return inner ? ` (${inner})` : '';
      };
      const num = (v: unknown): string =>
        v == null || v === '' ? '?' : String(v);
      const secs = (ms: unknown): string =>
        typeof ms === 'number' && Number.isFinite(ms)
          ? `${Math.round(ms / 1000)}s`
          : '?';
      const revText = (n: unknown): string => {
        if (typeof n !== 'number' || n <= 0) return '';
        return `${n} ${n === 1 ? 'reversión' : 'reversiones'}`;
      };

      // ── REVERSAL — the rising leg changed under us (only after confirm).
      // Checked before ENTRY because a reversal card keeps `toMode:
      // sweep_rising_leg_first`.
      if (reason === 'reversed') {
        const legs =
          p.oldRisingLeg || p.newRisingLeg
            ? `${p.oldRisingLeg ?? '?'} → ${p.newRisingLeg ?? '?'}`
            : '';
        const ceil =
          p.reTargetCeiling != null
            ? `techo re-target ${dollars(p.reTargetCeiling)}`
            : '';
        const detail = [legs, ceil].filter(Boolean).join(', ');
        const tail = `Sweep revertido${pathSuffix()}${detail ? ` — ${detail}` : ''}`;
        return [pair || evt.ruleId || '', tail].filter(Boolean).join(sep);
      }

      // ── ENTRY — rising-leg-first sweep activated.
      if (to === 'sweep_rising_leg_first') {
        const bits: string[] = [];
        if (p.risingLeg) bits.push(`subiendo ${p.risingLeg}`);
        if (p.contractsSwept != null) {
          const price = p.frontPrice != null ? ` @ ${p.frontPrice}` : '';
          bits.push(`${num(p.contractsSwept)} contratos barridos${price}`);
        }
        if (p.velocityTicksPer100ms != null) {
          bits.push(`${num(p.velocityTicksPer100ms)} ticks/100ms`);
        }
        // `inferred: true` is a materially weaker signal (the rising leg was
        // inferred from the complement, not observed) — mark it so the
        // operator can tell it apart from an authoritative classification.
        if (p.inferred === true) bits.push('(inferido)');
        const detail = bits.join(', ');
        const tail = `Sweep activado${pathSuffix()}${detail ? ` — ${detail}` : ''}`;
        return [pair || evt.ruleId || '', tail].filter(Boolean).join(sep);
      }

      // ── EXIT (rising-leg-first) — a real stop reason ended the sweep.
      // Only when leaving `sweep_rising_leg_first`; the legacy `sweep_dual_post`
      // exit keeps its own wording below.
      if (
        from === 'sweep_rising_leg_first' &&
        (to === 'maker_first' || to === 'paused' || SWEEP_STOP_REASONS.has(reason))
      ) {
        // The rising-leg-first sweep drives NO engine take/hedge (the rising leg
        // trades via the ordinary maker-first flow), so there is no
        // taken/hedged/remainder accounting to report — only the roles + how
        // long the episode lived + how many role reversals it saw.
        const legs =
          p.risingLeg || p.fallingLeg
            ? `subió ${p.risingLeg ?? '?'} / cayó ${p.fallingLeg ?? '?'}`
            : '';
        const segments = [
          `Sweep terminado${pathSuffix()}`,
          `${reason || 'stop'} tras ${secs(p.durationMs)}`,
        ];
        if (legs) segments.push(legs);
        const rev = revText(p.reversals);
        if (rev) segments.push(rev);
        const tail = `${segments[0]} — ${segments.slice(1).join(' · ')}`;
        return [pair || evt.ruleId || '', tail].filter(Boolean).join(sep);
      }

      // ── LEGACY `sweep_dual_post` overlay (deleted 2026-07) + naked exit.
      let tail: string;
      if (reason === 'naked_remainder' || to === 'paused') {
        tail = `Sweep sin cubrir — hedge en reposo, sweep bloqueado${pathSuffix()}`;
      } else if (to === 'sweep_dual_post') {
        tail = `Sweep dual-post activado${pathSuffix()}`;
      } else if (to === 'maker_first') {
        tail = `Sweep dual-post desactivado${pathSuffix(reason || undefined)}`;
      } else {
        // Unrecognized / legacy payload without the new keys — never crash:
        // prefer any human string the backend already formatted.
        const arrow = from && to ? `${from} → ${to}` : to || from || '';
        const legacy =
          arrow ||
          (typeof p.title === 'string' && p.title) ||
          (typeof p.message === 'string' && p.message) ||
          (typeof p.detail === 'string' && p.detail) ||
          'mode change';
        return [pair || evt.ruleId || '', legacy, arrow ? reason : '']
          .filter(Boolean)
          .join(sep);
      }
      return [pair || evt.ruleId || '', tail].filter(Boolean).join(sep);
    }
    case 'hedge_unhedged_panic':
      // Out-of-session hedge-check watchdog stopped a rule whose legs
      // drifted unhedged — paused it + cancelled its orders.
      return [
        pair || evt.ruleId || '',
        p.unhedgedQty != null ? `${p.unhedgedQty} un-hedged` : 'unhedged',
        p.reason ?? 'rule paused + orders cancelled',
      ]
        .filter(Boolean)
        .join(sep);
    case 'pair_missing_token_ids':
      // Fail-closed token-id invariant: the engine refused to trade a
      // rule whose pair lacks Polymarket yes/no token ids.
      return [
        pair || evt.ruleId || '',
        'pair missing Polymarket token ids',
        p.reason ?? p.detail ?? 'rule refused',
      ]
        .filter(Boolean)
        .join(sep);
    case 'unresolved_fill_naked_leg':
      // An executed Polymarket fill the engine could not attribute —
      // never dropped: the rule is paused and its orders cancelled.
      return [
        pair || evt.ruleId || '',
        p.qty != null ? `${p.qty} filled, side unresolved` : 'unresolved fill',
        p.reason ?? 'rule paused + orders cancelled',
      ]
        .filter(Boolean)
        .join(sep);
    case 'dead_trade_naked_leg': {
      // A booked (and hedged) trade DIED at the venue — the surviving leg
      // is naked; the engine already attempted a re-hedge and paused.
      const rehedge =
        p.rehedgeOutcome === 'placed' || p.rehedgeOutcome === 'rested'
          ? [
              `re-hedge ${p.rehedgeOutcome}`,
              p.rehedgeQty != null && p.rehedgePrice != null
                ? `${p.rehedgeQty} @ ${dollars(p.rehedgePrice)}`
                : '',
              p.rehedgeOrderId ? `order ${p.rehedgeOrderId}` : '',
            ]
              .filter(Boolean)
              .join(' ')
          : p.rehedgeOutcome === 'declined'
            ? `re-hedge declined${p.declineReason ? ` (${p.declineReason})` : ''}`
            : p.rehedgeOutcome === 'nothing_to_rehedge'
              ? 'nothing to re-hedge'
              : '';
      return [
        pair || evt.ruleId || '',
        p.path ? `path ${p.path}` : '',
        p.deadQty != null
          ? `${p.deadQty} booked dead on ${p.platform ?? '?'}`
          : 'booked trade died',
        p.survivingQty != null
          ? `surviving ${p.survivingQty}${
              p.survivingPlatform ? ` ${p.survivingPlatform}` : ''
            }${
              p.survivingAvgCost != null
                ? ` @ ${dollars(p.survivingAvgCost)} avg`
                : ''
            }`
          : '',
        rehedge,
        p.paused ? 'rule paused' : '',
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'orphaned_automation_fill':
      // The account-wide watcher's naked-leg net: an automation order
      // filled with no owning rule / no live engine — nobody is hedging it.
      return [
        p.marketTitle || p.marketId || '',
        p.filledQty != null
          ? `${p.filledQty} filled, no owning rule/engine`
          : 'orphaned automation fill',
        p.platform ?? '',
        p.reason ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'rule_state_stale':
      // CRITICAL fail-closed halt: rule state went unconfirmed past the
      // staleness threshold, so the engine HALTED all new maker/counter
      // placement. Process-wide (no rule/pair), but each engine is scoped to
      // one opener group — surface WHICH group + the stale window + blast
      // radius so the operator can tell groups apart in the feed.
      return [
        'Trading halted — rule state unconfirmed, placement paused',
        p.groupId ? `group ${p.groupId}` : '',
        p.staleSecs != null ? `stale ${p.staleSecs}s` : '',
        p.haltedRules != null ? `${p.haltedRules} rule(s) halted` : '',
        p.detail ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'rule_state_recovered':
      // The all-clear: rule state confirmed fresh again, placement resumed.
      return [
        'Trading resumed — rule state recovered',
        p.groupId ? `group ${p.groupId}` : '',
        p.detail ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'book_cross_saved':
      // The engine verified a book-cross against get_order: the real fill
      // was smaller than assumed, so it withheld the over-hedge.
      return [
        platform,
        pair || evt.ruleId || '',
        'Over-hedge avoided — book crossed but fill was smaller',
        p.actualQty != null && p.assumedQty != null
          ? `${p.actualQty} filled (assumed ${p.assumedQty})`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'over_hedged':
      // The hedge/counter leg over-bought vs the maker leg — real money
      // exposure the operator must reconcile.
      return [
        platform,
        pair || evt.ruleId || '',
        p.excess != null
          ? `over-hedged by ${p.excess} (${p.counterTotal ?? '?'} vs ${p.makerTotal ?? '?'})`
          : 'over-hedged',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_credit_applied':
      // A hedge was reduced because excess contracts from a previous
      // cycle already covered part of it.
      return [
        platform,
        pair || evt.ruleId || '',
        p.covered != null
          ? `hedge covered by prior excess: ${p.covered}`
          : 'hedge covered by prior excess',
        p.remainingCredit != null ? `credit left ${p.remainingCredit}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    // ─── Polymarket cancel path ────────────────────────────────────────
    // These payloads lead with `orderId` + `marketId`, each a 0x + 64 hex
    // string, so the default key-walk truncates at its 120-char budget
    // before reaching `attempts` / `elapsedMs` — the numbers that say how
    // bad it is. The engine's own actionable sentence lives in `message`,
    // which the row never renders, so without these cases the operator's
    // only view of the event is a hex dump.
    case 'poly_cancel_unconfirmed':
      return [
        // This family carries `makerPlatform`; read that. (The publisher is
        // being widened to emit `platform` too, so the row stays filterable
        // in the ops feed — but `makerPlatform` is the key that names the
        // leg and is present either way.) Same idiom as the counter-walk
        // case above.
        p.makerPlatform ?? platform,
        pair || evt.ruleId || '',
        p.unreadableAttempts != null && Number(p.unreadableAttempts) > 0
          ? `cancel UNRESOLVED — venue unreadable ${p.unreadableAttempts} rounds`
          : `cancel UNCONFIRMED — still resting after ${p.attempts ?? '?'} re-cancels`,
        p.elapsedMs != null ? `${p.elapsedMs}ms` : '',
        p.orderId ? `order ${shortId(String(p.orderId))}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'poly_cancel_abandoned':
      return [
        // This family carries `makerPlatform`; read that. (The publisher is
        // being widened to emit `platform` too, so the row stays filterable
        // in the ops feed — but `makerPlatform` is the key that names the
        // leg and is present either way.) Same idiom as the counter-walk
        // case above.
        p.makerPlatform ?? platform,
        pair || evt.ruleId || '',
        `cancel ABANDONED after ${p.attempts ?? '?'} re-cancels — leg held closed, cancel it by hand at the venue`,
        p.elapsedMs != null ? `${p.elapsedMs}ms` : '',
        p.orderId ? `order ${shortId(String(p.orderId))}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    // ─── Slack delivery health (2026-08-24) ──────────────────────────────
    // These three are the ONLY place a Slack outage is readable: they are
    // routed to mongo + realtime and never to Slack, because an alarm about
    // Slack sent over Slack cannot arrive. Without a case the row falls to the
    // generic `k=v` dump, which truncates at 120 chars under `nowrap` +
    // ellipsis — and the number that matters is the one that gets cut.
    case 'slack_delivery_failing': {
      // Field ORDER is the whole design here. The row is one line with
      // `whiteSpace: nowrap` + `textOverflow: ellipsis`, so anything past
      // ~130 chars is never read. The variable half — the status the venue
      // actually returned — goes FIRST; the constant editorial phrase, which
      // is identical on every one of these cards, goes last where losing it
      // to the ellipsis costs nothing.
      // Same rule as `messagesLost` below: `typeof`, not coercion. `true`
      // would otherwise read as a streak of 1 and print "1 POST rechazado",
      // asserting a count from a boolean.
      const failures =
        typeof p.consecutiveFailures === 'number' &&
        Number.isFinite(p.consecutiveFailures)
          ? p.consecutiveFailures
          : null;
      // With a 429 the engine alarms on the FIRST failure by design, so this
      // counter is 1 on the path that actually matters. "1 POST seguidos"
      // reads as an isolated blip over a quota that is gone.
      const streak =
        failures === null
          ? '? POST rechazados'
          : failures === 1
            ? '1 POST rechazado'
            : `${failures} POST seguidos rechazados`;
      const status = p.lastStatus ? `HTTP ${p.lastStatus}` : '';
      const rawError = p.lastError ? String(p.lastError) : '';
      // Strip the generic axios prefix; keep whatever the venue appended.
      const detail = rawError
        .replace(/^Request failed with status code \d+\s*(—\s*)?/, '')
        .trim();
      return [
        'SLACK NO ENTREGA',
        status,
        streak,
        // Only when it adds something the status line did not: the real
        // emitter's message is "Request failed with status code 429", which
        // would just reprint `HTTP 429` and eat ~40 of the ~130 readable
        // chars. The venue's own reason (`message_limit_exceeded` vs
        // `rate_limited`) IS worth the room — that is the one word telling
        // the operator whether waiting helps.
        detail ? `motivo: ${detail}` : '',
        // Every card produced while this is firing is destroyed on arrival —
        // a failed POST is discarded, there is no retry and no queue.
        'toda tarjeta de Slack se está perdiendo, fills incluidos',
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'slack_budget_warning':
      return [
        'CUOTA DE SLACK',
        // "sólo este pod" is not a hedge, it is the measurement. `used`/`max`
        // is one process's own counter while the Slack quota is workspace-wide
        // and several pods post to the same webhook — the backend documents it
        // as a tripwire, not an accountant. Dropping the qualifier would
        // present an estimate as the quota, which is the same invented number
        // the `messagesLost` guard refuses to produce one card over.
        `${p.percentUsed ?? '?'}% usado (${p.used ?? '?'}/${p.max ?? '?'} en ${p.windowHours ?? '?'}h, sólo este pod)`,
        // The distinction that decides what the operator does: this one still
        // delivers. `slack_delivery_failing` does not.
        'todavía entrega — recortá volumen antes de que corte',
      ]
        .filter(Boolean)
        .join(sep);
    case 'slack_delivery_recovered': {
      // `messagesLost` is REAL data loss, not a retry backlog: those cards were
      // discarded on failure and exist only here, in this feed. Leading with it
      // is the point — a green all-clear that hides the loss reads as "nothing
      // happened" over the exact opposite.
      //
      // An ABSENT count must never render as "nothing was lost". That is a
      // positive claim derived from missing data, on the one card whose entire
      // reason to exist is that number — and it is the direction that costs
      // the operator an investigation they will never know to open. Only an
      // explicit 0 earns the all-clear.
      // `typeof raw === 'number'`, NOT `Number(raw)`: `Number('')`,
      // `Number([])` and `Number(false)` are all a finite 0, so coercing would
      // hand the all-clear to three shapes that carry no count at all. Same
      // reasoning as `readNumber` in the backend's `slack-budget.ts`.
      const raw = p.messagesLost;
      const lost = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
      return [
        'SLACK VOLVIÓ',
        lost === null
          ? 'cantidad de tarjetas perdidas DESCONOCIDA — revisá el feed'
          : lost > 0
            ? `se perdieron ${lost} tarjeta${lost === 1 ? '' : 's'} — NO se reenvían, quedaron sólo acá`
            // DEFENSIVE, not the normal path: today's emitter cannot produce
            // 0 — `onDeliveryRecovered` only runs once the failure alarm has
            // fired, and the streak is >= 1 by then. Kept so a future emitter
            // that reports a clean recovery renders something true.
            : 'sin tarjetas perdidas',
      ]
        .filter(Boolean)
        .join(sep);
    }

    // The venue-maintenance lifecycle. These rows exist to be READ at a glance
    // at 4am: what venue, what window, and whether the drain left anything
    // resting. Without the cases the row is a payload dump and the operator is
    // back to guessing — which is the failure this whole feature removes.
    case 'venue_maintenance_halt': {
      // The ALERT, fired the moment the halt keys are armed and BEFORE the
      // account-wide cancel — so it deliberately carries no counts. What the
      // cancel achieved arrives on the `venue_maintenance_panic` card below.
      const armFailures = Array.isArray(p.haltArmFailures)
        ? p.haltArmFailures
        : [];
      // Observe-only (`VENUE_MAINTENANCE_ENFORCE=false`): the watcher called the
      // window and touched nothing. Saying "engines halted" here would be a flat
      // lie about the one thing the operator has to decide.
      if (p.enforced === false) {
        return [
          String(p.venue ?? ''),
          p.preemptive
            ? `MAINTENANCE in ${Math.max(0, Math.round(Number(p.leadSeconds ?? 0) / 60))}min — NOTHING halted, NOTHING cancelled (watcher is observe-only)`
            : 'MAINTENANCE already open — NOTHING halted, NOTHING cancelled (watcher is observe-only)',
          p.title ? String(p.title) : '',
          '🚨 both engines are still placing into it — stop them by hand if you want them out',
          p.endsAt
            ? `until ${new Date(String(p.endsAt)).toLocaleTimeString()}`
            : '',
        ]
          .filter(Boolean)
          .join(sep);
      }
      // A card stored BEFORE this shape existed: the drain ran inside the alert
      // and its counts rode along. Those docs are still in the feed and there is
      // no `venue_maintenance_panic` following them, so their counts have to be
      // printed HERE or they are lost — including `still resting`, the one
      // number that decided whether the operator had to go cancel by hand.
      // Their copy also has to stay THEIR truth: that window ran the
      // identity-scoped automation sweep, never `cancelEverything`, so telling
      // the operator today that every resting order was cancelled back then —
      // manual ones included — would be a fabricated history.
      const legacy =
        p.kalshiCancelled !== undefined || p.polymarketCancelled !== undefined;
      if (legacy) {
        return [
          String(p.venue ?? ''),
          p.preemptive
            ? `MAINTENANCE in ${Math.max(0, Math.round(Number(p.leadSeconds ?? 0) / 60))}min — engines halted, automation orders cancelled`
            : 'MAINTENANCE already open — engines halted, automation orders cancelled',
          p.title ? String(p.title) : '',
          `cancelled ${p.kalshiCancelled ?? '?'} Kalshi / ${p.polymarketCancelled ?? '?'} Poly`,
          armFailures.length > 0
            ? `🚨 HALT NOT ARMED: ${armFailures.join('; ')}`
            : '',
          describeCancelLeftovers(p) ?? 'venue confirmed empty',
          p.endsAt
            ? `until ${new Date(String(p.endsAt)).toLocaleTimeString()}`
            : '',
        ]
          .filter(Boolean)
          .join(sep);
      }
      return [
        String(p.venue ?? ''),
        p.preemptive
          ? `MAINTENANCE in ${Math.max(0, Math.round(Number(p.leadSeconds ?? 0) / 60))}min — GLOBAL PANIC: both engines halted, cancelling every resting order`
          : 'MAINTENANCE already open — GLOBAL PANIC: both engines halted, cancelling every resting order',
        p.title ? String(p.title) : '',
        // The loudest thing this card can say: a direction the watcher could
        // not halt is still placing INTO the window.
        armFailures.length > 0
          ? `🚨 HALT NOT ARMED: ${armFailures.join('; ')}`
          : '',
        p.endsAt ? `until ${new Date(String(p.endsAt)).toLocaleTimeString()}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'venue_maintenance_panic': {
      // The receipt. `verified` is an AND of two independently re-read facts —
      // both halt keys present AND both venues confirmed empty — so this row
      // never has to be interpreted: it either says the stop was confirmed, or
      // it says exactly which half was not.
      const halts = (p.haltsArmed ?? {}) as Record<string, unknown>;
      const notHalted = ['opener', 'unwinder'].filter((d) => halts[d] !== true);
      return [
        String(p.venue ?? ''),
        p.verified
          ? 'PANIC VERIFIED — everything cancelled, both engines halted'
          : '🚨 PANIC NOT VERIFIED — do NOT assume trading is stopped',
        // Immediately after the verdict, not fifth: the row is one line with
        // `textOverflow: ellipsis`, so at a glance the operator reads the head
        // and never reaches a 🚨 parked further along.
        Array.isArray(p.stillArmed) && p.stillArmed.length > 0
          ? `🚨 rules NOT paused on: ${p.stillArmed.join(' + ')} — halted but still ARMED`
          : '',
        `cancelled ${p.kalshiCancelled ?? '?'} Kalshi / ${p.polymarketCancelled ?? '?'} Poly`,
        describeCancelLeftovers(p) ?? 'both venues confirmed empty',
        notHalted.length > 0
          ? `🚨 halt NOT confirmed on: ${notHalted.join(' + ')}`
          : 'both engines confirmed halted',
        // The clean line needs BOTH: a count, and the field that would have named
        // a side. `rulesPaused` is a SUM across directions, so on its own it is a
        // healthy-looking number over a direction that is fully armed — and with
        // `stillArmed` absent (older publisher, dropped payload) the row printed
        // exactly that, while the sibling cards fail closed on the same question.
        Array.isArray(p.stillArmed) && p.stillArmed.length > 0
          ? ''
          : typeof p.rulesPaused === 'number' && Array.isArray(p.stillArmed)
            ? `panic scope: ${p.rulesPaused} rules paused · groups parked · account-wide cancel (failures below)`
            : '🚨 rules NOT confirmed paused — halted but possibly still ARMED',
        p.haltLiftedByOperator
          ? 'the operator lifted the halt while this ran — the keys are GONE, though what it paused stays paused'
          : '',
        p.elapsedMs != null ? `${(Number(p.elapsedMs) / 1000).toFixed(1)}s` : '',
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'venue_maintenance_window_over':
      return [
        String(p.venue ?? ''),
        // Observe-only means the WATCHER armed nothing — not that no key was
        // standing. The control deliberately keeps its halt-lift in that posture
        // precisely because one can be, so a card saying "nothing was halted"
        // swallows a gate the operator is about to remove.
        p.enforced === false
          ? 'maintenance OVER — the watcher armed nothing for it, nothing to resume (check the halt chips for what IS armed)'
          : !p.panicFired
            ? '🚨 maintenance OVER — halted, with NO panic recorded: assume the rules were never paused, so lifting this halt re-posts every maker'
            : p.panicDisarmed !== true
              ? '🚨 maintenance OVER — halted, but the panic did NOT disarm both directions: whichever side(s) it missed go back on the book when this halt lifts'
              : 'maintenance OVER — still halted, and the window fired a PANIC: nothing comes back until the groups are started and re-armed',
        p.title ? String(p.title) : '',
        p.heldSeconds != null
          ? p.enforced === false
            ? `venue was down ${Math.round(Number(p.heldSeconds) / 60)}min`
            : `halted ${Math.round(Number(p.heldSeconds) / 60)}min so far`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'venue_maintenance_resumed':
      return [
        String(p.venue ?? ''),
        // The halt-lift is never OFFERED in observe-only unless a key is
        // standing, so every event of this shape was pressed over one. "Nothing
        // had been halted" described a live gate that had just refused to lift.
        p.enforced === false
          ? p.haltCleared
            ? '🚨 halt key CLEARED on a window the watcher armed nothing for — no panic ran, so assume nothing had paused the rules'
            : 'halt-lift requested on a window the watcher armed nothing for — the key was NOT cleared (check the halt chips for what IS armed)'
          : !p.haltCleared
            ? p.panicFired && p.panicDisarmed === true
              ? 'halt-lift requested but the key was NOT cleared — the rules stay paused and the group pods down either way'
              : 'halt-lift requested but the key was NOT cleared'
            : !p.panicFired
              ? '🚨 halt LIFTED with NO panic recorded — assume nothing had paused the rules, so assume every maker this gate held back is on the book now'
              : p.panicDisarmed !== true
                ? '🚨 halt LIFTED over rules that were NEVER confirmed paused — makers may be back on the book right now'
                : 'halt LIFTED — nothing resumed by it: start each group\'s pods and re-arm its rules',
        p.title ? String(p.title) : '',
        p.heldSeconds != null
          ? `held ${Math.round(Number(p.heldSeconds) / 60)}min`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'venue_maintenance_probe_blind':
      return [
        String(p.venue ?? ''),
        `status feed UNREADABLE ${p.consecutiveFailures ?? '?'} passes in a row — maintenance protection is BLIND (this did not itself halt anything)`,
        p.error ? String(p.error) : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'poly_pending_cancel_registry_overflow':
      return [
        `${p.entries ?? '?'} un-confirmed Polymarket cancels outstanding (bound ${p.maxEntries ?? '?'})`,
        'nothing evicted — every entry keeps its leg closed',
      ]
        .filter(Boolean)
        .join(sep);
    case 'poly_replace_withheld':
      return [
        // This family carries `makerPlatform`; read that. (The publisher is
        // being widened to emit `platform` too, so the row stays filterable
        // in the ops feed — but `makerPlatform` is the key that names the
        // leg and is present either way.) Same idiom as the counter-walk
        // case above.
        p.makerPlatform ?? platform,
        pair || evt.ruleId || '',
        'replacement WITHHELD — cancel not confirmed off-book; leg PARKED and will not re-place on its own',
        p.orderId ? `order ${shortId(String(p.orderId))}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    // ─── Forfait protection ────────────────────────────────────────────
    // The pair collapsed to a 50-50 resolution regime. The row has to answer
    // the three operator questions at a glance: which paths stopped, WHY, and
    // what is still on the table — including the fact that the existing
    // position was deliberately left alone.
    case 'forfait_protection_triggered': {
      const paths = Array.isArray(p.suspendedPaths) && p.suspendedPaths.length
        ? `paths ${p.suspendedPaths
            .map((x: unknown) => String(x).toUpperCase())
            .join('+')} suspendidos`
        : 'sin paths suspendidos';
      // Never fall THROUGH to the pin wording: an absent or unknown trigger
      // would render a divergence the event never carried. `pin_divergence` is
      // the RETIRED price-only detector — no new card carries it, but the
      // archive still holds the ones it emitted, so its branch stays.
      const reason =
        p.trigger === 'consecutive_cycles'
          ? `${p.cyclesUnderFloor ?? '?'} ciclos < piso`
          : p.trigger === 'pin_divergence'
            ? `pin 0.50 · div ${
                p.divergence != null ? Number(p.divergence).toFixed(2) : '?'
              }`
            : `motivo desconocido${p.trigger ? ` (${p.trigger})` : ''}`;
      return [
        pair || evt.ruleId || '',
        // A later path crossing the cap emits an UPDATE, not a new episode.
        p.update ? 'actualización' : '',
        paths,
        reason,
        `Poly ${dollars(p.polyMid)} · Kalshi ${dollars(p.kalshiMid)} · techo ${dollars(p.polyCap)}`,
        `${p.contractsAtRisk ?? 0} contratos — la posición NO se desarmó`,
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'forfait_hold_started': {
      const secs = p.holdMs != null ? Math.round(Number(p.holdMs) / 1000) : null;
      // The stall re-report carries `heldMs`: NOT a second hold and NOT a
      // verdict — the brake is still on and nothing is deciding. Two different
      // faults wear that symptom and the row has to tell them apart, because
      // the fix differs: an unreadable book is a feed problem, while a readable
      // one means the hold keeps re-baselining on a market too quiet for its
      // window. Saying "no readable book" for both would send the operator
      // chasing a feed that is fine.
      if (p.heldMs != null) {
        const held = Math.round(Number(p.heldMs) / 1000);
        const blind = Math.round(Number(p.unreadableMs ?? p.heldMs) / 1000);
        // The engine reports whether the book reads AT the stall tick. Deriving
        // it from the two ages instead calls a feed that died halfway through
        // healthy, which sends the operator to the wrong place.
        const unreadable =
          p.bookReadable != null ? !p.bookReadable : blind >= held / 2;
        return [
          pair || evt.ruleId || '',
          unreadable
            ? `sin libro de Poly legible hace ${blind}s (frenada hace ${held}s)`
            : `frenada hace ${held}s sin veredicto — el libro SÍ se lee, llega muy espaciado`,
          'la entrada sigue frenada',
        ]
          .filter(Boolean)
          .join(sep);
      }
      return [
        pair || evt.ruleId || '',
        `${p.cycles ?? '?'} ciclos < ${dollars(p.sumFloor)}`,
        `Poly ${dollars(p.polyPrice)} · techo ${dollars(p.polyCap)}`,
        secs != null ? `entrada frenada ${secs}s — se decide con el libro` : '',
        'nada pausado, nada desarmado',
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'forfait_hold_dropped': {
      // No verdict was taken: the hold was let go because the rule stopped
      // holding, not because the engine judged the book. Rendering the released
      // wording here would tell the operator a collapse was ruled out when
      // nobody looked — and this row is the only place either event shows up.
      return [
        pair || evt.ruleId || '',
        'hold soltado sin veredicto — se limpió forfaitHoldMs o se apagó la protección',
        p.baselinePoly != null
          ? `última lectura de Poly ${dollars(p.baselinePoly)}`
          : 'nunca llegó a leer el libro',
        'NO dice nada sobre si el par está colapsando',
      ]
        .filter(Boolean)
        .join(sep);
    }
    case 'forfait_hold_released': {
      return [
        pair || evt.ruleId || '',
        `Poly ${dollars(p.baselinePoly)} → ${dollars(p.poly)}`,
        'dejó de ir a 0.50 — freno liberado, la regla vuelve a entrar',
      ]
        .filter(Boolean)
        .join(sep);
    }
    default: {
      // Defensive: an event type we don't have an explicit case for
      // (newer backend than frontend). Walk the top-level payload
      // keys and render `key=value` pairs so the operator still
      // gets a readable line — never a JSON dump.
      const pairs: string[] = [];
      for (const [k, v] of Object.entries(p)) {
        if (v == null) continue;
        if (typeof v === 'object') continue;
        pairs.push(`${k}=${v}`);
        if (pairs.join(' ').length > 120) break;
      }
      return pairs.join(' ') || '(no payload)';
    }
  }
}

// Memoised so a live-WS coalesce tick (which rebuilds the parent feed
// every ~100ms under load) only re-renders the rows whose `evt` reference
// actually changed, not every visible row. `evt` objects keep a stable
// identity across rebuilds (they come straight from the react-query pages
// / live overlay), so the default shallow prop compare is correct.
const NotificationRow = memo(function NotificationRow({
  evt,
  repeatCount = 1,
}: {
  evt: NotificationEvent;
  /** `×N` multiplicity from `collapseModeChangeRuns` — 1 hides the badge. */
  repeatCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const display = getNotificationDisplay(evt.type);
  // Accent normally follows the per-type color, but a `mode_change` naked /
  // hedge-failure EXIT reads as an error, not the sweep blue.
  const accentColor = resolveNotificationColor(evt);
  const direction = getNotificationDirection(evt);
  const directionDisplay = DIRECTION_DISPLAY[direction];
  return (
    <Paper
      variant="outlined"
      sx={{
        // Tight vertical rhythm so more rows fit on screen — the
        // feed is meant to be scanned, not read line by line.
        // Horizontal padding is asymmetric: extra room on the
        // right so the timestamp column doesn't crowd the edge.
        py: 0.5,
        pl: 1.5,
        pr: 1.5,
        mb: 0.5,
        borderLeft: 3,
        borderLeftColor: accentColor,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: 'pointer', minHeight: 28 }}
      >
        <Box
          aria-hidden
          sx={{
            fontSize: '1rem',
            lineHeight: 1,
            minWidth: 22,
            textAlign: 'center',
          }}
        >
          {display.emoji}
        </Box>
        <Chip
          size="small"
          label={display.label ?? evt.type}
          variant="outlined"
          // Custom height/font so the chip stays visually balanced
          // with the row's compact line-height. MUI's `size="small"`
          // chip is 24px tall — slightly too large after the row
          // padding shrunk.
          sx={{
            borderColor: accentColor,
            color: accentColor,
            fontWeight: 500,
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        {/* `×N` multiplicity badge — a collapsed run of identical
            `mode_change` cards (visual backstop for a sweep-latch
            regression; should never appear once the engine latch lands). */}
        {repeatCount > 1 && (
          <Chip
            size="small"
            label={`×${repeatCount}`}
            sx={{
              backgroundColor: accentColor,
              color: 'common.white',
              fontWeight: 700,
              height: 20,
              fontSize: '0.7rem',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        {/* Direction chip — Entry / Unwind / System. Filled (not
            outlined) so it reads as a small badge and doesn't compete
            with the outlined type / severity chips for visual weight.
            System events suppress the chip — they're direction-less
            and the absence of a chip is itself the signal. */}
        {direction !== 'system' && (
          <Chip
            size="small"
            label={directionDisplay.label}
            sx={{
              backgroundColor: directionDisplay.color,
              color: 'common.white',
              fontWeight: 600,
              height: 20,
              fontSize: '0.7rem',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
        <Chip
          size="small"
          label={evt.severity}
          color={severityChipColor(evt.severity)}
          variant="outlined"
          sx={{
            textTransform: 'capitalize',
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Chip
          size="small"
          label={evt.source}
          variant="outlined"
          sx={{
            textTransform: 'capitalize',
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontSize: '0.8rem',
            lineHeight: 1.3,
            // Truncate gracefully — the operator clicks to expand
            // for the full payload anyway.
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {colorizePlatforms(summarizeEvent(evt))}
        </Typography>
        {/* Per-platform market deep-links — mirrors the Slack message
            builder's `marketLinks()` so both surfaces point at the same
            page. Click stops propagation so the row's expand/collapse
            toggle doesn't fire when the operator just wants to open
            the exchange. */}
        {notificationMarketLinks(evt).map((link) => (
          <IconButton
            key={link.platform}
            size="small"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`Open on ${link.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'}`}
            sx={{
              color: PLATFORM_COLORS[link.platform],
              p: 0.25,
              '& svg': { fontSize: 14 },
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        ))}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
        >
          {formatTime(evt.occurredAt)}
        </Typography>
      </Stack>
      {open && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: 'background.default',
            borderRadius: 1,
          }}
        >
          {(() => {
            const custom = renderCustomExpandedDetail(evt);
            if (custom) return custom;
            return (
              <Stack spacing={0.5}>
                {eventDetailRows(evt).map(([label, value]) => (
                  <Stack
                    key={label}
                    direction="row"
                    spacing={2}
                    alignItems="flex-start"
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 200,
                        color: 'text.secondary',
                        flexShrink: 0,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'common.white',
                        wordBreak: 'break-word',
                        flex: 1,
                      }}
                    >
                      {value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            );
          })()}
        </Box>
      )}
    </Paper>
  );
});

function NotificationsFeedTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<NotificationsFilter>(() =>
    readFilterFromParams(searchParams),
  );
  const [livePrepended, setLivePrepended] = useState<NotificationEvent[]>([]);
  // Live-WS coalesce buffer + flush timer (see the consume effect
  // below). Declared up here so the filter/reconnect reset effects can
  // also drain them when they clear the overlay.
  const liveBufferRef = useRef<NotificationEvent[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset the live overlay AND drain any in-flight coalesce batch.
  // Used by the filter-change + reconnect effects so a buffered batch
  // built under a stale filter (or before a reconnect refetch) can't
  // flush into the fresh view.
  const resetLiveOverlay = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    liveBufferRef.current = [];
    setLivePrepended([]);
  }, []);

  const { data: typesData } = useNotificationTypes();
  // Stabilise the array ref so dependent useMemos don't recompute on
  // every render (`typesData?.types ?? []` creates a fresh array).
  const allTypes = useMemo(
    () => (typesData?.types ?? []).filter((t) => !isSocialNotification(t)),
    [typesData],
  );

  // Collapse `allTypes` into chip-label groups so the Type filter shows
  // ONE entry per chip label (one "filled", one "partial", everything
  // else one-to-one). See `TypeFilterGroup` for the rationale.
  const typeFilterGroups = useMemo(
    () => buildTypeFilterGroups(allTypes),
    [allTypes],
  );

  // Derive group selection from the type list in `filter.types`. A
  // group counts as selected if ANY of its member types is in the
  // filter — this stays robust when a URL ships only a subset (e.g.
  // legacy bookmark with one type) and avoids a tristate UI.
  const selectedGroupKeys = useMemo(() => {
    const sel = new Set(filter.types ?? []);
    if (sel.size === 0) return [] as string[];
    return typeFilterGroups
      .filter((g) => g.types.some((t) => sel.has(t)))
      .map((g) => g.key);
  }, [filter.types, typeFilterGroups]);

  // Translate group selection back into a flat type list when the
  // user changes the dropdown. Selecting "filled" expands to every
  // member type so the backend filter still works at the type
  // granularity it understands.
  const applyGroupSelection = useCallback(
    (groupKeys: string[]) => {
      const keySet = new Set(groupKeys);
      const types: NotificationEventType[] = [];
      for (const g of typeFilterGroups) {
        if (keySet.has(g.key)) types.push(...g.types);
      }
      setFilter((f) => ({
        ...f,
        types: types.length > 0 ? types : undefined,
      }));
    },
    [typeFilterGroups],
  );

  const queryFilter = useMemo(
    () => ({
      ...filter,
      excludeTypes: [...(filter.excludeTypes ?? []), ...SOCIAL_NOTIFICATION_TYPES],
    }),
    [filter],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
  } = useNotifications(queryFilter);

  // Persist filter in URL. The writer builds a FRESH param set from the
  // notifications filter only, so carry over the `tab` selection AND the
  // sibling Live-match-events tab's `ev*` params — otherwise switching to this
  // tab strips them and the events tab reverts to defaults on return.
  useEffect(() => {
    const next = writeFilterToParams(filter);
    searchParams.forEach((value, key) => {
      if (key === 'tab' || key.startsWith('ev')) next.append(key, value);
    });
    setSearchParams(next, { replace: true });
    // Reset live overlay when filters change — old events may not
    // match the new query.
    resetLiveOverlay();
  }, [filter, searchParams, setSearchParams, resetLiveOverlay]);

  // Live append from WS.
  const { queueVersion, drain, isConnected, gapVersion } =
    useNotificationsSocket();
  // Canonical newest-first feed order: `occurredAt` (event time — what the
  // operator reads) PRIMARY, the global `streamId` as the same-timestamp
  // tiebreak across rules, per-rule `seq` as the final fallback. Pure +
  // unit-tested in notificationsFeed. Module-level fn → stable identity, so
  // the useCallback dep arrays below don't churn.
  //
  // It is the SAME primary key (occurredAt) for both the live overlay and the
  // REST history, which is what keeps the two-way merge ordered — sorting by
  // streamId first (Redis INGEST order, ≠ event time) put older-timestamp
  // rows above newer ones and produced an ascending run at the live/REST seam.
  const compareEventsDesc = compareNotificationsDesc;
  // Audio cues are NOT wired here anymore — they're mounted app-wide via
  // `PredictionNotificationSound` in SidebarNav. This page still consumes
  // the socket queue below for its LIVE FEED prepend, but calling
  // `useNotificationSound` here too would double-ring the cue.

  // Live WS prepend, coalesced into ~100ms windows. A busy engine
  // bursts dozens of events/sec; applying one `setLivePrepended` per
  // event would re-render (and re-sort the combined `rows` memo) on
  // every frame. Instead we buffer arrivals in a ref and flush the
  // whole batch on a single timer tick. The operator can't perceive
  // a sub-100ms delay, and the feed stays smooth under load.
  const LIVE_COALESCE_MS = 100;
  // Hard ceiling on the in-memory live overlay so a long, never-
  // reconnecting session can't grow it unbounded — the REST pages
  // (react-query) are the durable backing store; older live rows are
  // always re-fetchable. The overlay only needs to cover the gap
  // since the last REST page. Capped FIFO (newest kept).
  const MAX_LIVE_PREPENDED = 500;
  // Latest filter, read at push time without re-subscribing the WS
  // consume effect every time the filter object identity changes.
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const flushLiveBuffer = useCallback(() => {
    flushTimerRef.current = null;
    const batch = liveBufferRef.current;
    if (batch.length === 0) return;
    liveBufferRef.current = [];
    setLivePrepended((rows) => {
      // Ordered insert per buffered event (occurredAt desc, seq desc)
      // rather than naive prepend. WS arrival order is NOT a reliable
      // proxy for source order: when two events for the same rule fire
      // in the same JS tick on the producer (canonical: counter_filled
      // then execution_complete), their dispatcher.publish() Promise.all
      // fan-outs interleave and the Redis publishes / React batches can
      // deliver them in either order. Sorting on insert decouples render
      // order from WS arrival order — never re-sort the whole array.
      const next = rows.slice();
      for (const evt of batch) insertDescending(next, evt, compareEventsDesc);
      // FIFO-cap the overlay (keep the newest MAX_LIVE_PREPENDED).
      return next.length > MAX_LIVE_PREPENDED
        ? next.slice(0, MAX_LIVE_PREPENDED)
        : next;
    });
  }, [compareEventsDesc]);

  useEffect(() => {
    if (queueVersion === 0) return;
    // Drain the WHOLE socket queue — every `notification` frame received
    // since the last drain (no single-slot overwrite, so a same-tick
    // burst is never dropped). Read the current filter from the ref so a
    // filter change doesn't re-run this effect (the filter-change effect
    // already resets the overlay).
    const batch = drain();
    if (batch.length === 0) return;
    let queued = false;
    // The queue is live-tagged for the AUDIO gate (`QueuedNotification.live`);
    // the feed shows every frame, replay included — that is what closes the
    // gap after a disconnect.
    for (const { event: evt } of batch) {
      if (matchesFilter(evt, filterRef.current)) {
        liveBufferRef.current.push(evt);
        queued = true;
      }
    }
    if (queued && flushTimerRef.current == null) {
      flushTimerRef.current = setTimeout(flushLiveBuffer, LIVE_COALESCE_MS);
    }
  }, [queueVersion, drain, flushLiveBuffer]);

  // Drop any pending buffered batch + timer on unmount.
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  // Coarse REST fallback for an UN-recoverable gap. The durable Redis
  // Stream + cursor replay is now the primary recovery: on every
  // reconnect the socket re-subscribes with its `lastStreamId` and the
  // gateway replays every event since it, so the per-page "refetch the
  // first page on reconnect" hack (which only patched a single page-
  // sized window) is GONE. We only top-up from REST when the FE detects
  // a gap it can't self-heal from the stream — the cursor was trimmed
  // past the stream floor, surfaced as a `gapVersion` bump. Mongo is the
  // durable record; `mergeNotificationFeed` dedupes by `id`, so the
  // REST/replay overlap is harmless and we do NOT clear the overlay.
  useEffect(() => {
    if (gapVersion === 0) return;
    refetch();
  }, [gapVersion, refetch]);

  // Combined feed: live overlay + react-query pages, deduped by id and
  // kept newest-first by (occurredAt desc, seq desc) — the same key the
  // backend uses, so the overlay and the historical page agree on order
  // even when a live event arrives whose timestamp is older than the
  // most-recent REST page (rare, but possible after a tab-sleep reconnect
  // patches the gap).
  //
  // Both inputs are already individually sorted desc, so this is a linear
  // two-way MERGE rather than a full re-sort of the whole array on every
  // ~100ms coalesce tick — and the result is capped at MAX_FEED_ROWS so
  // the virtualizer count, DOM measurement cache, and per-tick work stay
  // bounded no matter how many older pages have been loaded. Older rows
  // remain in Mongo, reachable via the time-range / Pair / Rule filters.
  const rows = useMemo<NotificationEvent[]>(
    () =>
      mergeNotificationFeed(
        livePrepended,
        data?.pages.map((page) => page.items) ?? [],
        compareEventsDesc,
        MAX_FEED_ROWS,
      ),
    [livePrepended, data, compareEventsDesc],
  );

  // Collapse consecutive identical `mode_change` cards into a single row
  // with a `×N` badge (visual backstop for a sweep-latch regression; a
  // no-op — every `repeatCount === 1` — once the engine latch lands).
  const feedRows = useMemo<CollapsedFeedRow[]>(
    () => collapseModeChangeRuns(rows),
    [rows],
  );

  // Virtualize the feed so only the ~visible rows mount. Each row is a
  // MUI <Paper> with multiple <Chip>s + an expandable detail panel, so
  // hundreds of rows would otherwise be tens of thousands of DOM nodes
  // plus a linearly-growing Emotion style cache (the same pattern the
  // unwinder/opener tables use — see project_fe_perf_patterns). Rows
  // have variable height (collapsed ~36px, expanded much taller), so we
  // attach `measureElement` to each rendered row and let react-virtual's
  // ResizeObserver re-measure on expand/collapse rather than estimating.
  const feedScrollRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: feedRows.length,
    getScrollElement: () => feedScrollRef.current,
    estimateSize: () => 40,
    overscan: 10,
    getItemKey: (index) => feedRows[index]?.evt.id ?? index,
  });

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* `Box` is a column flex so the filter panel keeps its natural
          height and the feed below it gets the remaining space as its
          own scroll container (required for the virtualizer's
          getScrollElement). */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Notifications
        </Typography>
        <Chip
          size="small"
          color={isConnected ? 'success' : 'default'}
          label={isConnected ? 'live' : 'disconnected'}
        />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => refetch()} title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        {/*
          Three explicit rows so the filter labels never collide
          with the time-range chip Box (the previous flex-wrap
          layout overlapped on narrow widths because the chip
          group is taller than a single TextField/Select).

          Row 1 — Time range chips (full-width row).
          Row 2 — Free-text + Type multi-select (3 equal columns).
          Row 3 — Platform / Source / Severity / Clear (4 cols).

          All 2nd/3rd-row items use `flex: 1, minWidth: 160` so
          they expand to fill, but break to a column on xs.
        */}
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}
            >
              Time range
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PRESET_RANGES.map((r) => {
                const active =
                  r.minutes == null
                    ? !filter.from
                    : filter.from ===
                      new Date(
                        Math.floor(Date.now() / 60_000) * 60_000 -
                          r.minutes * 60_000,
                      ).toISOString();
                // Active match is approximate (rounded to minute) so
                // the chip stays highlighted even after a render pass.
                return (
                  <Chip
                    key={r.label}
                    label={r.label}
                    size="small"
                    variant={active ? 'filled' : 'outlined'}
                    color={active ? 'primary' : 'default'}
                    onClick={() =>
                      setFilter((f) => ({
                        ...f,
                        from:
                          r.minutes == null
                            ? undefined
                            : new Date(
                                Date.now() - r.minutes * 60_000,
                              ).toISOString(),
                        to: undefined,
                      }))
                    }
                  />
                );
              })}
            </Stack>
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <TextField
              label="Pair Id"
              size="small"
              value={filter.pairId ?? ''}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  pairId: e.target.value || undefined,
                }))
              }
              sx={{ flex: 1, minWidth: 160 }}
            />
            <TextField
              label="Rule Id"
              size="small"
              value={filter.ruleId ?? ''}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  ruleId: e.target.value || undefined,
                }))
              }
              sx={{ flex: 1, minWidth: 160 }}
            />
            <FormControl size="small" sx={{ flex: 1, minWidth: 220 }}>
              <InputLabel id="type-filter">Type</InputLabel>
              <Select
                multiple
                labelId="type-filter"
                label="Type"
                value={selectedGroupKeys}
                onChange={(e) => {
                  const v =
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',').filter(Boolean)
                      : (e.target.value as string[]);
                  applyGroupSelection(v);
                }}
                renderValue={(sel) => {
                  const keys = sel as string[];
                  if (keys.length === 0) return 'Any';
                  return keys
                    .map(
                      (k) =>
                        typeFilterGroups.find((g) => g.key === k)?.emoji ??
                        '',
                    )
                    .join(' ');
                }}
              >
                {typeFilterGroups.map((g) => {
                  // Direction is filtered by the dedicated Direction
                  // dropdown, not here. A multi-type group (e.g. the
                  // `filled` group bundling maker/counter/crossing/
                  // recovery/manual fills) shows a neutral "N kinds"
                  // hint; a single-type group needs none.
                  const hintLabel =
                    g.types.length > 1 ? `${g.types.length} kinds` : '';
                  const hintColor = '#9e9e9e';
                  return (
                    <MenuItem key={g.key} value={g.key}>
                      <Box
                        sx={{ minWidth: 28, textAlign: 'center', mr: 1 }}
                        aria-hidden
                      >
                        {g.emoji}
                      </Box>
                      <Box
                        sx={{ color: g.color, fontWeight: 500, flex: 1 }}
                      >
                        {g.label}
                      </Box>
                      <Box
                        sx={{
                          fontSize: '0.65rem',
                          color: hintColor,
                          fontWeight: 600,
                          ml: 1,
                        }}
                      >
                        {hintLabel}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id="direction-filter">Direction</InputLabel>
              <Select
                labelId="direction-filter"
                label="Direction"
                value={filter.direction ?? 'all'}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    direction: e.target.value as any,
                  }))
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="entry">Entry (open)</MenuItem>
                <MenuItem value="unwind">Unwind (close)</MenuItem>
                <MenuItem value="system">System</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id="platform-filter">Platform</InputLabel>
              <Select
                labelId="platform-filter"
                label="Platform"
                value={filter.platform ?? 'all'}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    platform: e.target.value as any,
                  }))
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="kalshi">Kalshi</MenuItem>
                <MenuItem value="polymarket">Polymarket</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id="source-filter">Source</InputLabel>
              <Select
                labelId="source-filter"
                label="Source"
                value={filter.source ?? 'all'}
                onChange={(e) =>
                  setFilter((f) => ({ ...f, source: e.target.value as any }))
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="automation">🤖 Automation</MenuItem>
                <MenuItem value="manual">✋ Manual</MenuItem>
                <MenuItem value="system">⚙️ System</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id="severity-filter">Severity</InputLabel>
              <Select
                labelId="severity-filter"
                label="Severity"
                value={filter.severity ?? 'all'}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    severity: e.target.value as any,
                  }))
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ alignSelf: { md: 'center' }, whiteSpace: 'nowrap', ml: 0 }}
              control={
                <Switch
                  size="small"
                  checked={!filter.excludeTypes?.length}
                  onChange={(e) =>
                    setFilter((f) => ({
                      ...f,
                      excludeTypes: e.target.checked
                        ? undefined
                        : LIFECYCLE_NOISE_TYPES,
                    }))
                  }
                />
              }
              label="Show order lifecycle"
            />
            <Button
              size="small"
              onClick={() =>
                setFilter({
                  direction: 'all',
                  platform: 'all',
                  source: 'all',
                  severity: 'all',
                  // Keep lifecycle noise hidden on Clear — matches the default.
                  excludeTypes: LIFECYCLE_NOISE_TYPES,
                })
              }
              sx={{ alignSelf: { md: 'center' }, minWidth: 80 }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          Failed to load notifications.
        </Alert>
      )}

      {isFetching && rows.length === 0 ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading…</Typography>
        </Stack>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No notifications match the current filters.
        </Typography>
      ) : (
        // Scroll container for the virtualizer. Takes the remaining
        // flex space below the filter panel; only the rows inside the
        // visible window (+ overscan) actually mount.
        <Box ref={feedScrollRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <Box
            sx={{
              height: rowVirtualizer.getTotalSize(),
              position: 'relative',
              width: '100%',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((vi) => {
              const row = feedRows[vi.index];
              if (!row) return null;
              return (
                <Box
                  key={vi.key}
                  data-index={vi.index}
                  ref={rowVirtualizer.measureElement}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vi.start}px)`,
                  }}
                >
                  <NotificationRow evt={row.evt} repeatCount={row.repeatCount} />
                </Box>
              );
            })}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="center" sx={{ pb: 2 }}>
            {rows.length >= MAX_FEED_ROWS ? (
              // The feed holds its memory cap of newest rows — fetching
              // older pages would only grow the cache for rows that get
              // sliced off anyway. Point the operator at the filters,
              // which are the feed's real "go further back" controls.
              <Typography variant="caption" color="text.secondary">
                Showing the newest {MAX_FEED_ROWS.toLocaleString()} —
                narrow the time range or Pair / Rule filters to see older
                notifications.
              </Typography>
            ) : hasNextPage ? (
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load older'}
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                End of feed.
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function matchesFilter(
  evt: NotificationEvent,
  filter: NotificationsFilter,
): boolean {
  if (isSocialNotification(evt.type)) return false;
  if (filter.types && filter.types.length > 0 && !filter.types.includes(evt.type))
    return false;
  // Hidden types (e.g. amends) must not slip in via the realtime channel —
  // mirror the REST `excludeType` filter.
  if (
    filter.excludeTypes &&
    filter.excludeTypes.length > 0 &&
    filter.excludeTypes.includes(evt.type)
  )
    return false;
  if (
    filter.direction &&
    filter.direction !== 'all' &&
    getNotificationDirection(evt) !== filter.direction
  )
    return false;
  if (
    filter.platform &&
    filter.platform !== 'all' &&
    evt.platform !== filter.platform
  )
    return false;
  if (filter.source && filter.source !== 'all' && evt.source !== filter.source)
    return false;
  if (
    filter.severity &&
    filter.severity !== 'all' &&
    evt.severity !== filter.severity
  )
    return false;
  if (filter.ruleId && evt.ruleId !== filter.ruleId) return false;
  if (filter.pairId && evt.pairId !== filter.pairId) return false;
  if (filter.from) {
    const t = new Date(filter.from).getTime();
    if (new Date(evt.occurredAt).getTime() < t) return false;
  }
  return true;
}

/** The two tabs on this page. `notifications` is the default. */
type NotificationsPageTab = 'notifications' | 'events';

/**
 * Page shell: a `<Tabs>` bar over the existing notifications feed and the
 * new Live match events tab. The active tab is persisted in the `?tab=`
 * URL param (no route added — `Router.tsx` is untouched). Only the active
 * tab's panel is mounted, so each panel's own filter-in-URL effect runs
 * in isolation. The notifications feed is UNCHANGED — it's rendered as-is
 * from `NotificationsFeedTab`.
 */
export function NotificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: NotificationsPageTab =
    searchParams.get('tab') === 'events' ? 'events' : 'notifications';

  const handleTabChange = (_: unknown, next: NotificationsPageTab) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', next);
        return p;
      },
      { replace: true },
    );
  };

  return (
    <Box sx={{ height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{
          px: 3,
          pt: 1,
          flexShrink: 0,
          minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0,
            textTransform: 'none',
            fontSize: '0.85rem',
          },
          '& .MuiTabs-indicator': { height: 2 },
        }}
      >
        <Tab value="notifications" label="Notifications" />
        <Tab value="events" label="Live match events" />
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {tab === 'notifications' ? <NotificationsFeedTab /> : <LiveMatchEventsTab />}
      </Box>
    </Box>
  );
}
